import React, { useState, useEffect, useCallback } from 'react';
import { CreativeStructuredDetail } from '../components/CreativeStructuredDetail';
import { MetaAccountChips, MetaRunInfo } from '@/components/MetaAccountChips';
import { useMetaSync, metaApi } from '@/components/MetaAdAccountsCard';
import DateRangeFilter from '../components/DateRangeFilter';
import IntelligenceReportModal from '../components/IntelligenceReportModal';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Brand colors mapping
const BRAND_COLORS = {
  'Wellversed': '#10B981',
  'Soulful': '#8B5CF6', 
  'Cosmix': '#EC4899',
  'Betterhalf': '#F59E0B',
  'default': '#10B981'
};

// Lazy loading media component for performance (images & videos)
// Uses the browser's native `loading="lazy"` — no JSON fetch, no base64.
const LazyMedia = ({ itemId, filename, contentType, isVideo, onViewDetails }) => {
  const [videoData, setVideoData] = React.useState(null);
  const [error, setError] = React.useState(false);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [loadingVideo, setLoadingVideo] = React.useState(false);
  const mediaRef = React.useRef(null);
  const videoRef = React.useRef(null);

  const loadAndPlayVideo = async (e) => {
    e.stopPropagation();
    
    if (videoData) {
      // Video already loaded, just play/pause
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
      return;
    }

    // Load video data
    setLoadingVideo(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/creative-analyses/${itemId}/video`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVideoData(data);
        setIsPlaying(true);
        // Auto-play after loading
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play();
          }
        }, 100);
      } else {
        toast.error('Video playback not available for this file');
      }
    } catch (err) {
      toast.error('Failed to load video');
    } finally {
      setLoadingVideo(false);
    }
  };

  return (
    <div ref={mediaRef} className="w-full h-full relative">
      {error ? (
        <div className="w-full h-full flex items-center justify-center bg-slate-100">
          {isVideo ? <Film className="h-8 w-8 text-slate-300" /> : <FileImage className="h-8 w-8 text-slate-300" />}
        </div>
      ) : (
        isVideo ? (
          <div className="relative w-full h-full group bg-black">
            {/* Show video player if loaded */}
            {videoData ? (
              <video 
                ref={videoRef}
                src={
                  videoData.video_url
                    ? `${process.env.REACT_APP_BACKEND_URL}${videoData.video_url}`
                    : `data:${videoData.content_type};base64,${videoData.video_base64}`
                }
                // Preserve the video's native aspect ratio (letterbox on the
                // dark container instead of stretching / cropping vertical
                // videos to fill the card). Also disables the picture-in-
                // picture button and download control-menu entry so the
                // in-card player stays intentionally minimal.
                className="w-full h-full object-contain bg-black"
                controls
                controlsList="nodownload"
                disablePictureInPicture
                playsInline
                onClick={(e) => e.stopPropagation()}
                onEnded={() => setIsPlaying(false)}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
              />
            ) : (
              <>
                {/* Video thumbnail (first frame) — direct raw URL with native lazy loading */}
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/creative-analyses/${itemId}/image/raw`}
                  alt={filename}
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover"
                  onError={() => setError(true)}
                />
                {/* Play button - center */}
                <div 
                  className="absolute inset-0 flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={loadAndPlayVideo}
                    className="w-14 h-14 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                  >
                    {loadingVideo ? (
                      <Loader2 className="h-6 w-6 text-violet-600 animate-spin" />
                    ) : (
                      <Play className="h-6 w-6 text-slate-700 ml-1" />
                    )}
                  </button>
                </div>
                {/* View Details button - bottom right */}
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    size="sm" 
                    className="bg-white text-slate-900 hover:bg-slate-100 shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onViewDetails) onViewDetails();
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" /> View Analysis
                  </Button>
                </div>
              </>
            )}
            {/* Video badge */}
            <div className="absolute top-2 left-2 px-2 py-0.5 bg-violet-600 text-white text-[10px] font-medium rounded flex items-center gap-1 z-10">
              <Film className="h-3 w-3" /> Video
            </div>
          </div>
        ) : (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/creative-analyses/${itemId}/image/raw`}
            alt={filename}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={() => setError(true)}
          />
        )
      )}
    </div>
  );
};

// Keep old LazyImage for backward compatibility
const LazyImage = ({ itemId, filename, contentType }) => {
  return <LazyMedia itemId={itemId} filename={filename} contentType={contentType} isVideo={false} />;
};

// Inline Ad Name editor (compact) — used in list rows
const AdNameInlineEditor = ({ item, onSave }) => {
  const value = item?.metrics?.ad_name || '';
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(value);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setVal(value); }, [value]);

  const commit = async () => {
    if ((val || '').trim() === (value || '').trim()) { setEditing(false); return; }
    setSaving(true);
    await onSave(item.id, val.trim(), item.metrics);
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') { setVal(value); setEditing(false); } if (e.key === 'Enter') commit(); }}
          placeholder="Ad name..."
          className="w-full text-sm rounded px-2 py-0.5 border border-[#2EB4AC] bg-white focus:outline-none focus:ring-1 focus:ring-[#2EB4AC]/30"
          data-testid={`adname-input-${item.id}`}
        />
        {saving && <span className="text-[10px] text-[#2EB4AC] animate-pulse">·</span>}
      </div>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="w-full text-left rounded px-1 py-0.5 hover:bg-slate-100 border border-transparent hover:border-slate-200 transition"
      title={value ? `Edit: ${value}` : 'Click to add ad name'}
      data-testid={`adname-display-${item.id}`}
    >
      {value ? (
        <span className="text-sm text-slate-700 block truncate">{value}</span>
      ) : (
        <span className="text-slate-300 text-sm italic">Add ad name…</span>
      )}
    </button>
  );
};

// Inline Learning editor (compact) - used in list rows
const LearningInlineEditor = ({ itemId, value, onSave, compact = false }) => {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(value || '');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { setVal(value || ''); }, [value]);

  const commit = async () => {
    if ((val || '').trim() === (value || '').trim()) { setEditing(false); return; }
    setSaving(true);
    await onSave(itemId, val.trim());
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-start gap-1" onClick={(e) => e.stopPropagation()}>
        <textarea
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit(); }}
          placeholder="Add learning / insight..."
          className={`w-full ${compact ? 'text-[11px] min-h-[42px] px-2 py-1' : 'text-xs min-h-[56px] px-2 py-1.5'} rounded border border-amber-300 bg-amber-50/50 focus:outline-none focus:ring-1 focus:ring-amber-400`}
          data-testid={`learning-input-${itemId}`}
        />
        {saving && <span className="text-[10px] text-amber-600 animate-pulse">Saving</span>}
      </div>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`w-full text-left ${compact ? 'text-[11px] min-h-[24px]' : 'text-xs min-h-[28px]'} rounded px-2 py-1 hover:bg-amber-50 border border-transparent hover:border-amber-200 transition group flex items-start gap-1`}
      data-testid={`learning-display-${itemId}`}
    >
      <Lightbulb className={`h-3 w-3 flex-shrink-0 mt-0.5 ${value ? 'text-amber-500' : 'text-slate-300 group-hover:text-amber-400'}`} />
      {value ? (
        <span className="text-slate-700 line-clamp-2">{value}</span>
      ) : (
        <span className="text-slate-400 italic group-hover:text-amber-600">Add learning…</span>
      )}
    </button>
  );
};

import { 
  Loader2, 
  Sparkles,
  Eye,
  Search,
  FileImage,
  LayoutGrid,
  List,
  Cpu,
  Calendar,
  User,
  Trash2,
  CheckSquare,
  X,
  TrendingUp,
  IndianRupee,
  Users,
  MapPin,
  Target,
  BarChart3,
  Edit3,
  Check,
  Brain,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Zap,
  Wand2,
  AlertTriangle,
  Palette,
  Type,
  Image as ImageIcon,
  Settings,
  Clock,
  History,
  ClipboardList,
  Rocket,
  Upload,
  FileSpreadsheet,
  Download,
  Copy,
  FileText,
  BookMarked,
  Save,
  Trash,
  Film,
  Play,
  Tag,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AGE_GROUPS = ['18-24', '25-34', '35-44', '45-54', '55+'];
const GENDERS = ['Male', 'Female', 'All'];
const LOCATIONS = ['Pan India', 'North', 'South', 'East', 'West', 'Metro Cities', 'Tier 1', 'Tier 2'];

// Competitor brands for competitive analysis
const COMPETITOR_BRANDS = ['MuscleBlaze', 'Superyou', 'ON', 'GNC'];

// Helper to format date in IST
const formatDateIST = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    // Add 5:30 hours for IST if the date is in UTC
    return date.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      day: 'numeric', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return dateStr;
  }
};

const CreativeLogPage = () => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTheme, setFilterTheme] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterCompetitor, setFilterCompetitor] = useState('all');
  const [filterRoas, setFilterRoas] = useState('all');
  const [filterMediaType, setFilterMediaType] = useState('all'); // 'all', 'image', 'video'
  // Date range on `created_at` (upload date). `{from:null,to:null}` = Lifetime.
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const lbFrames = selectedCreative?.analyzed_frames || [];
  const lightboxFrame = lightboxIdx !== null ? lbFrames[lightboxIdx] : null;
  const closeLightbox = () => setLightboxIdx(null);
  const prevFrame = useCallback(() => {
    setLightboxIdx((i) => (i === null || lbFrames.length === 0 ? i : (i - 1 + lbFrames.length) % lbFrames.length));
  }, [lbFrames.length]);
  const nextFrame = useCallback(() => {
    setLightboxIdx((i) => (i === null || lbFrames.length === 0 ? i : (i + 1) % lbFrames.length));
  }, [lbFrames.length]);
  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prevFrame();
      else if (e.key === 'ArrowRight') nextFrame();
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIdx, prevFrame, nextFrame]);
  const [settingThumbnail, setSettingThumbnail] = useState(false);
  const setFrameAsThumbnail = async () => {
    if (!selectedCreative?.id || lightboxIdx === null) return;
    setSettingThumbnail(true);
    try {
      const r = await fetch(`${API_URL}/api/creative-analyses/${selectedCreative.id}/thumbnail`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ frame_index: lightboxIdx }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Failed to set thumbnail');
      toast.success(`Frame ${lbFrames[lightboxIdx]?.label} set as thumbnail`);
      // Optimistic update on the table-state
      setHistory(prev => prev.map(it => it.id === selectedCreative.id ? { ...it, thumbnail_frame_index: lightboxIdx } : it));
      setSelectedCreative(prev => prev ? { ...prev, thumbnail_frame_index: lightboxIdx } : prev);
      // Refetch history so LazyMedia for that card re-renders with the new image_base64
      fetchHistory();
    } catch (e) {
      toast.error(e.message || 'Failed to set thumbnail');
    } finally {
      setSettingThumbnail(false);
    }
  };
  const [deleting, setDeleting] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkBrand, setBulkBrand] = useState('');
  const [bulkCompetitor, setBulkCompetitor] = useState('');
  const [usdToInr, setUsdToInr] = useState(83.5);
  const [brands, setBrands] = useState([]);
  const [allThemes, setAllThemes] = useState([]);
  const [allCompetitors, setAllCompetitors] = useState([]);
  const [allUsedBrands, setAllUsedBrands] = useState([]);
  const [showCostDetails, setShowCostDetails] = useState(false);
  const [editingMetrics, setEditingMetrics] = useState(null);
  const [metricsForm, setMetricsForm] = useState({});
  const [savingMetrics, setSavingMetrics] = useState(false);
  
  // Insights state
  const [insights, setInsights] = useState(null);
  const [insightsCache, setInsightsCache] = useState({}); // Cache insights by brand
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsProgress, setInsightsProgress] = useState(0); // Progress 0-100
  const [insightsStep, setInsightsStep] = useState(''); // Current step text
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [insightsError, setInsightsError] = useState(null);
  const [insightsBrand, setInsightsBrand] = useState('all');
  const [roasThreshold, setRoasThreshold] = useState(3.0);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  
  // CSV Import state
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [importingAfterPreview, setImportingAfterPreview] = useState(false);

  // Meta Ads → Creative Log ROAS sync (background job, polled until done).
  const { running: syncingRoas, start: syncRoasFromMeta } = useMetaSync(() => fetchHistory());
  const [metaAccounts, setMetaAccounts] = useState([]);
  const [filterMetaAccount, setFilterMetaAccount] = useState('all');
  const [filterLive, setFilterLive] = useState('all');
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const refreshLiveStatus = async () => {
    setRefreshingStatus(true);
    try {
      const r = await metaApi('/meta-ads/refresh-status', {
        method: 'POST', body: selectedIds.length ? JSON.stringify({ creative_ids: selectedIds.slice(0, 25) }) : undefined,
      });
      toast.success(`Live status updated · ${r.live_creatives} of ${r.creatives_checked} creatives live`);
      fetchHistory();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRefreshingStatus(false);
    }
  };
  useEffect(() => {
    metaApi('/meta-accounts').then((d) => setMetaAccounts(d.accounts || [])).catch(() => {});
  }, []);

  // Pagination state for list view — server-side driven. `totalItems` comes
  // from the API so the page-count reflects ALL creatives, not just the ones
  // currently loaded into `history`.
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [totalItems, setTotalItems] = useState(0);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterBrand, filterTheme, filterRoas, filterCompetitor, filterMediaType, filterMetaAccount, filterLive, dateRange]);

  useEffect(() => {
    fetchStats();
    fetchExchangeRate();
    fetchBrands();
    fetchFilterOptions();
    fetchAnalysisHistory();
  }, []);

  // Server-side pagination — refetch whenever the page window or ANY
  // filter changes. All filters are now applied on the backend so
  // pagination reflects the full filtered dataset (not just the current page).
  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filterBrand, filterTheme, filterCompetitor, filterRoas, filterMediaType, filterMetaAccount, filterLive, dateRange]);

  // Debounced refetch for the free-text search input so we don't hammer
  // the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchHistory();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' });
      if (response.ok) setBrands(await response.json());
    } catch (error) {
      console.error('Failed to fetch brands:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/filter-options`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAllThemes(data.themes || []);
        setAllUsedBrands(data.brands || []);
        setAllCompetitors(data.competitors || []);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchInsights = async (brandFilter = insightsBrand, threshold = roasThreshold, creativeIds = null) => {
    setInsightsLoading(true);
    setInsightsError(null);
    setInsightsProgress(0);
    setInsightsStep('Starting analysis...');
    
    try {
      // Use selected IDs if provided, otherwise analyze all (filtered by brand)
      const idsToAnalyze = creativeIds || (selectedIds.length > 0 ? selectedIds : null);
      
      // Start background job
      const params = new URLSearchParams();
      if (brandFilter && brandFilter !== 'all') params.append('brand', brandFilter);
      params.append('roas_threshold', threshold.toString());
      
      const requestBody = idsToAnalyze ? { creative_ids: idsToAnalyze } : {};
      
      const startResponse = await fetch(`${API_URL}/api/creative-analyses/insights/start?${params}`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });
      
      if (!startResponse.ok) {
        const errorData = await startResponse.json().catch(() => ({}));
        setInsightsError(errorData.detail || `Error: ${startResponse.status}`);
        setInsights(null);
        setInsightsLoading(false);
        return;
      }
      
      const { job_id } = await startResponse.json();
      
      // Poll for results
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`${API_URL}/api/creative-analyses/insights/status/${job_id}`, {
            credentials: 'include'
          });
          
          if (!statusResponse.ok) {
            clearInterval(pollInterval);
            setInsightsError('Failed to check job status');
            setInsightsLoading(false);
            return;
          }
          
          const job = await statusResponse.json();
          setInsightsProgress(job.progress || 0);
          
          // Update step text based on progress
          if (job.progress < 30) {
            setInsightsStep('Gathering creative data...');
          } else if (job.progress < 50) {
            setInsightsStep('Analyzing performance metrics...');
          } else if (job.progress < 70) {
            setInsightsStep('Identifying patterns...');
          } else if (job.progress < 90) {
            setInsightsStep('Generating AI insights...');
          } else {
            setInsightsStep('Processing results...');
          }
          
          if (job.status === 'completed') {
            clearInterval(pollInterval);
            setInsightsProgress(100);
            setInsightsStep('Complete!');
            
            if (job.result) {
              setInsights(job.result);
              setInsightsCache(prev => ({ ...prev, [brandFilter]: job.result }));
              setInsightsExpanded(true);
            }
            
            setTimeout(() => {
              setInsightsLoading(false);
              setInsightsProgress(0);
              setInsightsStep('');
            }, 500);
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            setInsightsError(job.error || 'Analysis failed');
            setInsights(null);
            setInsightsLoading(false);
          }
        } catch (pollError) {
          console.error('Poll error:', pollError);
        }
      }, 1000); // Poll every 1 second
      
      // Safety timeout after 2 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (insightsLoading) {
          setInsightsError('Analysis timed out. Please try again.');
          setInsightsLoading(false);
        }
      }, 120000);
      
    } catch (error) {
      console.error('Failed to start insights:', error);
      setInsightsError('Failed to start analysis. Please try again.');
      setInsightsLoading(false);
    }
  };

  // Switch to cached insights when brand changes
  const handleBrandChange = (brand) => {
    setInsightsBrand(brand);
    if (insightsCache[brand]) {
      setInsights(insightsCache[brand]);
      setInsightsExpanded(true);
      setInsightsError(null);
    } else {
      // No cache for this brand - show message to analyze
      setInsights(null);
    }
  };

  const fetchAnalysisHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/history/list`, { credentials: 'include' });
      if (response.ok) {
        setAnalysisHistory(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch analysis history:', error);
    }
  };

  const loadHistoricalAnalysis = (historyItem) => {
    setInsights({
      statistical_insights: historyItem.statistical_insights,
      ai_insights: historyItem.ai_insights,
      analyzed_at: historyItem.created_at,
      analyzed_creatives: historyItem.analyzed_creatives
    });
    setRoasThreshold(historyItem.roas_threshold || 3);
    setInsightsExpanded(true);
    setShowHistoryDropdown(false);
  };

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      if (response.ok) {
        const data = await response.json();
        setUsdToInr(data.rates?.INR || 83.5);
      }
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
    }
  };

  const toINR = (usd) => (usd * usdToInr).toFixed(2);

  // CSV Preview handler - shows preview before import
  const handleCsvPreview = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvFile(file);
    setCsvUploading(true);
    setCsvPreview(null);
    setCsvResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/creative-analyses/preview-metrics-csv`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const result = await response.json();
      setCsvPreview(result);
      
      if (result.matched_count === 0) {
        toast.warning('No matching creatives found in CSV');
      }
    } catch (error) {
      toast.error('Failed to preview CSV: ' + error.message);
      setCsvPreview({ success: false, error: error.message });
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  // Confirm and execute the actual import
  const confirmCsvImport = async () => {
    if (!csvFile) return;
    
    setImportingAfterPreview(true);
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await fetch(`${API_URL}/api/creative-analyses/import-metrics-csv`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const result = await response.json();
      setCsvResult(result);
      setCsvPreview(null);
      setCsvFile(null);
      
      if (result.updated_count > 0) {
        toast.success(`Successfully updated ${result.updated_count} creative(s) with metrics`);
        fetchHistory();
      }
    } catch (error) {
      toast.error('Failed to import CSV: ' + error.message);
      setCsvResult({ success: false, error: error.message });
    } finally {
      setImportingAfterPreview(false);
    }
  };

  // Cancel preview and reset
  const cancelCsvPreview = () => {
    setCsvPreview(null);
    setCsvFile(null);
    setCsvResult(null);
  };

  // Download sample CSV template
  const downloadCsvTemplate = () => {
    const headers = 'creative_id,ad_name,location,age_group,gender,spend,roas,audience,campaign_start_date';
    const sampleRow = 'CAMP_001,Summer Sale Banner,"Pan India","25-34,35-44",All,15000,3.5,Health Enthusiasts,2025-01-15';
    const csvContent = `${headers}\n${sampleRow}`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'metrics_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const fetchHistory = async () => {
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams();
      params.set('limit', String(itemsPerPage));
      params.set('skip', String(skip));
      if (filterBrand && filterBrand !== 'all') params.set('brand', filterBrand);
      if (filterTheme && filterTheme !== 'all') params.set('theme', filterTheme);
      if (filterCompetitor && filterCompetitor !== 'all') params.set('competitor', filterCompetitor);
      if (filterMediaType && filterMediaType !== 'all') params.set('media_type', filterMediaType);
      if (filterMetaAccount !== 'all') params.set('meta_account', filterMetaAccount);
      if (filterLive !== 'all') params.set('live_status', filterLive);
      if (filterRoas && filterRoas !== 'all') params.set('roas', filterRoas);
      if (searchQuery && searchQuery.trim()) params.set('search', searchQuery.trim());
      if (dateRange?.from) {
        const d = dateRange.from;
        params.set('date_from', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }
      if (dateRange?.to) {
        const d = dateRange.to;
        params.set('date_to', `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
      }
      const response = await fetch(`${API_URL}/api/creative-analyses?${params.toString()}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          // Legacy shape — no total available, fall back to length.
          setHistory(data);
          setTotalItems(data.length);
        } else {
          setHistory(data.items || []);
          setTotalItems(typeof data.total === 'number' ? data.total : (data.items || []).length);
        }
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/stats`, { credentials: 'include' });
      if (response.ok) setStats(await response.json());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const openCreativeDetail = async (item) => {
    // If image is missing, fetch full data
    if (!item.image_base64) {
      try {
        const response = await fetch(`${API_URL}/api/creative-analyses/${item.id}`, { credentials: 'include' });
        if (response.ok) {
          const fullData = await response.json();
          setSelectedCreative(fullData);
          return;
        }
      } catch (error) {
        console.error('Failed to fetch creative:', error);
      }
    }
    setSelectedCreative(item);
  };

  const deleteCreative = async (id, e) => {
    e?.stopPropagation();
    if (deleting) return;
    setDeleting(id);
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/${id}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (response.ok) {
        setHistory(prev => prev.filter(item => item.id !== id));
        setSelectedIds(prev => prev.filter(sid => sid !== id));
        if (selectedCreative?.id === id) setSelectedCreative(null);
        toast.success('Creative deleted');
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0 || bulkDeleting) return;
    setBulkDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedIds })
      });
      if (response.ok) {
        setHistory(prev => prev.filter(item => !selectedIds.includes(item.id)));
        toast.success(`${selectedIds.length} creatives deleted`);
        setSelectedIds([]);
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  const bulkUpdateBrand = async () => {
    if (selectedIds.length === 0 || !bulkBrand) return;
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/bulk-brand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedIds, brand: bulkBrand })
      });
      if (response.ok) {
        setHistory(prev => prev.map(item => 
          selectedIds.includes(item.id) ? { ...item, brand: bulkBrand } : item
        ));
        toast.success(`Brand updated for ${selectedIds.length} creatives`);
        setSelectedIds([]);
        setBulkBrand('');
      }
    } catch (error) {
      toast.error('Failed to update brand');
    }
  };

  const bulkUpdateCompetitor = async () => {
    if (selectedIds.length === 0 || !bulkCompetitor) return;
    const competitorValue = bulkCompetitor === 'clear' ? '' : bulkCompetitor;
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/bulk-competitor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedIds, competitor: competitorValue })
      });
      if (response.ok) {
        setHistory(prev => prev.map(item => 
          selectedIds.includes(item.id) ? { ...item, competitor: competitorValue } : item
        ));
        toast.success(bulkCompetitor === 'clear' ? `Competitor cleared for ${selectedIds.length} creatives` : `Competitor updated for ${selectedIds.length} creatives`);
        setSelectedIds([]);
        setBulkCompetitor('');
      }
    } catch (error) {
      toast.error('Failed to update competitor');
    }
  };

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelectedIds(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelectedIds(selectedIds.length === filteredHistory.length ? [] : filteredHistory.map(item => item.id));
  };

  const openMetricsEditor = (creative, e) => {
    e?.stopPropagation();
    setEditingMetrics(creative);
    setMetricsForm(creative.metrics || {});
  };

  const saveMetrics = async () => {
    if (!editingMetrics) return;
    setSavingMetrics(true);
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/${editingMetrics.id}/metrics`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(metricsForm)
      });
      if (response.ok) {
        setHistory(prev => prev.map(item => 
          item.id === editingMetrics.id ? { ...item, metrics: metricsForm } : item
        ));
        if (selectedCreative?.id === editingMetrics.id) {
          setSelectedCreative(prev => ({ ...prev, metrics: metricsForm }));
        }
        toast.success('Metrics saved');
        setEditingMetrics(null);
      }
    } catch (error) {
      toast.error('Failed to save metrics');
    } finally {
      setSavingMetrics(false);
    }
  };

  const saveLearning = async (id, learning) => {
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/${id}/learning`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ learning })
      });
      if (response.ok) {
        setHistory(prev => prev.map(item => item.id === id ? { ...item, learning } : item));
        if (selectedCreative?.id === id) {
          setSelectedCreative(prev => ({ ...prev, learning }));
        }
        toast.success('Learning saved');
      }
    } catch (error) {
      toast.error('Failed to save learning');
    }
  };

  const [rephrasingLearning, setRephrasingLearning] = useState(false);
  const rephraseLearning = async (id) => {
    setRephrasingLearning(true);
    try {
      const r = await fetch(`${API_URL}/api/creative-analyses/${id}/rephrase-learning`, {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Rephrase failed');
      toast.success('Actionable summary generated');
      setHistory(prev => prev.map(item => item.id === id ? { ...item, learning_rephrased: j.learning_rephrased, learning_rephrased_at: j.learning_rephrased_at } : item));
      if (selectedCreative?.id === id) {
        setSelectedCreative(prev => ({ ...prev, learning_rephrased: j.learning_rephrased, learning_rephrased_at: j.learning_rephrased_at }));
      }
    } catch (e) {
      toast.error(`Rephrase failed: ${e.message || ''}`);
    } finally {
      setRephrasingLearning(false);
    }
  };

  // ===== Re-analyze video =====
  // Kicks off a background re-analysis of an existing creative (server pulls
  // the source video from GridFS, re-runs the full pipeline, and merges the
  // fresh analysis + transcripts back onto the same doc). We poll the async
  // job status so the user gets a spinner + success toast without leaving
  // the page. Metrics / brand / competitor / learning are preserved.
  const [reanalyzingId, setReanalyzingId] = useState(null);
  const reanalyzeCreative = async (id) => {
    if (!id) return;
    setReanalyzingId(id);
    try {
      const r = await fetch(`${API_URL}/api/creative-analyses/${id}/reanalyze`, {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Re-analyze failed');
      const jobId = j.job_id;
      toast.info('Re-analyzing… this can take a minute for long videos');
      // Poll status every 3s, cap at ~5 min total.
      const start = Date.now();
      const poll = async () => {
        try {
          const sr = await fetch(`${API_URL}/api/analyze-video/status/${jobId}`, { credentials: 'include' });
          const sj = await sr.json();
          if (sj.status === 'success') {
            toast.success('Re-analysis complete');
            // Refresh the row + open popup with fresh data
            try {
              const fresh = await fetch(`${API_URL}/api/creative-analyses/${id}`, { credentials: 'include' });
              if (fresh.ok) {
                const doc = await fresh.json();
                setHistory(prev => prev.map(it => it.id === id ? { ...it, ...doc } : it));
                if (selectedCreative?.id === id) setSelectedCreative(doc);
              } else {
                fetchHistory();
              }
            } catch { fetchHistory(); }
            setReanalyzingId(null);
            return;
          }
          if (sj.status === 'error') {
            toast.error(`Re-analysis failed: ${sj.error || 'unknown error'}`);
            setReanalyzingId(null);
            return;
          }
          if (Date.now() - start > 5 * 60 * 1000) {
            toast.error('Re-analysis timed out — check the Creative Log later');
            setReanalyzingId(null);
            return;
          }
          setTimeout(poll, 3000);
        } catch (err) {
          toast.error(`Status check failed: ${err.message || ''}`);
          setReanalyzingId(null);
        }
      };
      setTimeout(poll, 2500);
    } catch (e) {
      toast.error(`Re-analyze failed: ${e.message || ''}`);
      setReanalyzingId(null);
    }
  };

  // ===== Analysis-from-learning (Learning view toggle) =====
  // Regenerates the structured Hook/CTA/Audio/Visual + meta row through the
  // lens of the marketer's saved learning. Server caches the result on the
  // doc and returns it directly on subsequent calls when the learning
  // text hasn't changed.
  const [learningAnalysisLoadingId, setLearningAnalysisLoadingId] = useState(null);
  const generateLearningAnalysis = async (id, force = false) => {
    if (!id) return;
    setLearningAnalysisLoadingId(id);
    try {
      const qs = force ? '?force=true' : '';
      const r = await fetch(`${API_URL}/api/creative-analyses/${id}/analysis-from-learning${qs}`, {
        method: 'POST', credentials: 'include',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed to generate learning view');
      const patch = {
        analysis_from_learning: j.analysis_from_learning,
        analysis_from_learning_at: j.generated_at,
        analysis_from_learning_learning_hash: j.learning_hash,
      };
      setHistory(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
      if (selectedCreative?.id === id) {
        setSelectedCreative(prev => ({ ...prev, ...patch }));
      }
      if (!j.cached) toast.success('Learning view generated');
    } catch (e) {
      toast.error(`Learning view failed: ${e.message || ''}`);
    } finally {
      setLearningAnalysisLoadingId(null);
    }
  };

  // ===== Consolidated Learnings =====
  const [consolidatedOpen, setConsolidatedOpen] = useState(false);
  const [consolidatedData, setConsolidatedData] = useState({ pointers: [], based_on_count: 0, generated_at: null, stale: false, infographic: null });
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  const [consolidatedRefreshing, setConsolidatedRefreshing] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  // Resolve which creatives are currently in scope. If user has hand-picked
  // creatives in the Creative Log table (`selectedIds`), use those directly.
  // Otherwise fall back to the active filter scope.
  const getConsolidatedScope = () => {
    const hasManualSelection = Array.isArray(selectedIds) && selectedIds.length > 0;
    let finalItems;
    if (hasManualSelection) {
      // Hand-picked from the table — use as-is, keeping only those with learnings
      const selSet = new Set(selectedIds);
      finalItems = (history || []).filter(item =>
        selSet.has(item.id) && !!(item.learning && String(item.learning).trim().length > 0)
      );
    } else {
      // Use current filter scope
      finalItems = (history || []).filter(item => {
        const matchesBrand = filterBrand === 'all' || item.brand === filterBrand;
        const matchesTheme = filterTheme === 'all' || item.analysis?.theme === filterTheme;
        const matchesRoas =
          filterRoas === 'all' ||
          (filterRoas === 'high' && item.metrics?.roas >= 3) ||
          (filterRoas === 'medium' && item.metrics?.roas >= 2 && item.metrics?.roas < 3) ||
          (filterRoas === 'low' && item.metrics?.roas < 2 && item.metrics?.roas > 0) ||
          (filterRoas === 'none' && !item.metrics?.roas);
        const q = (searchQuery || '').toLowerCase();
        const matchesQ = !q || (item.filename || '').toLowerCase().includes(q) || (item.brand || '').toLowerCase().includes(q);
        const matchesComp = filterCompetitor === 'all' || (item.is_competitor_ad ? 'competitor' : 'own') === filterCompetitor;
        const hasLearning = !!(item.learning && String(item.learning).trim().length > 0);
        return matchesBrand && matchesTheme && matchesRoas && matchesQ && matchesComp && hasLearning;
      });
    }
    const ids = finalItems.map(i => i.id);
    const parts = [];
    if (hasManualSelection) {
      parts.push(`${finalItems.length} selected from Creative Log`);
    } else {
      if (filterBrand !== 'all') parts.push(`Brand=${filterBrand}`);
      if (filterTheme !== 'all') parts.push(`Theme=${filterTheme}`);
      if (filterRoas !== 'all') parts.push(`ROAS=${filterRoas}`);
      if (filterCompetitor !== 'all') parts.push(`Type=${filterCompetitor}`);
      if (searchQuery) parts.push(`q="${searchQuery}"`);
    }
    const label = parts.length ? parts.join(' · ') : 'All creatives';
    return { ids, label, count: ids.length, items: finalItems, hasManualSelection };
  };

  const fetchConsolidated = async () => {
    setConsolidatedLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/learnings/consolidated`, { credentials: 'include' });
      const j = await r.json();
      if (r.ok) setConsolidatedData(j);
    } catch {
      // silently ignore — UI shows fallback
    } finally {
      setConsolidatedLoading(false);
    }
  };

  const refreshConsolidated = async () => {
    const scope = getConsolidatedScope();
    if (scope.ids.length === 0) {
      toast.error(scope.hasManualSelection
        ? 'None of the selected creatives have learnings yet'
        : 'No creatives with learnings match the current filters');
      return;
    }
    setConsolidatedRefreshing(true);
    const tid = toast.loading(`Synthesizing ${scope.count} learning${scope.count === 1 ? '' : 's'}…`);
    try {
      const r = await fetch(`${API_URL}/api/learnings/consolidated/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creative_ids: scope.ids,
          brand: filterBrand !== 'all' ? filterBrand : null,
          performer_filter: 'all',
          roas_threshold: roasThreshold || 3.0,
          scope_label: scope.label,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Refresh failed');
      setConsolidatedData({ ...j, scope_label: scope.label });
      toast.success(`Consolidated ${j.based_on_count} learning${j.based_on_count === 1 ? '' : 's'} into ${j.pointers.length} pointers`, { id: tid });
    } catch (e) {
      toast.error(e.message || 'Failed to refresh', { id: tid });
    } finally {
      setConsolidatedRefreshing(false);
    }
  };

  const openConsolidated = () => {
    setConsolidatedOpen(true);
    fetchConsolidated();
    fetchPlaybooks();
  };

  // ===== Saved Playbooks =====
  const [playbooks, setPlaybooks] = useState([]);
  const [savePlaybookOpen, setSavePlaybookOpen] = useState(false);
  const [playbookName, setPlaybookName] = useState('');
  const [playbookSaving, setPlaybookSaving] = useState(false);

  const fetchPlaybooks = async () => {
    try {
      const r = await fetch(`${API_URL}/api/learnings/playbooks`, { credentials: 'include' });
      if (r.ok) setPlaybooks(await r.json());
    } catch {
      // ignore
    }
  };

  const savePlaybook = async () => {
    const nm = (playbookName || '').trim();
    if (!nm) {
      toast.error('Give it a name');
      return;
    }
    if (!(consolidatedData.pointers || []).length) {
      toast.error('Generate pointers first');
      return;
    }
    setPlaybookSaving(true);
    try {
      const scope = getConsolidatedScope();
      const r = await fetch(`${API_URL}/api/learnings/playbooks`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nm,
          pointers: consolidatedData.pointers,
          scope_label: consolidatedData.scope_label || scope.label,
          brand: filterBrand !== 'all' ? filterBrand : null,
          based_on_count: consolidatedData.based_on_count || scope.count,
          top_performers_only: false,
          creative_ids: scope.ids,
          infographic: consolidatedData.infographic || null,
        }),
      });
      if (!r.ok) {
        const j = await r.json();
        throw new Error(j.detail || 'Save failed');
      }
      await fetchPlaybooks();
      setSavePlaybookOpen(false);
      setPlaybookName('');
      toast.success(`Saved as "${nm}"`);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setPlaybookSaving(false);
    }
  };

  const loadPlaybook = (pb) => {
    setConsolidatedData({
      pointers: pb.pointers || [],
      based_on_count: pb.based_on_count || 0,
      generated_at: pb.created_at,
      generated_by: pb.created_by,
      scope_label: pb.scope_label,
      stale: false,
      infographic: pb.infographic || null,
    });
    if (pb.top_performers_only !== undefined) { /* legacy field, no-op */ }
    toast.success(`Loaded "${pb.name}"`);
  };

  const deletePlaybook = async (pb) => {
    if (!window.confirm(`Delete playbook "${pb.name}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/learnings/playbooks/${pb.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Delete failed');
      await fetchPlaybooks();
      toast.success('Playbook deleted');
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const exportConsolidated = (kind) => {
    const pointers = consolidatedData.pointers || [];
    if (pointers.length === 0) {
      toast.error('Nothing to export yet — generate first');
      return;
    }
    const scope = consolidatedData.scope_label || 'All creatives';
    const stamp = consolidatedData.generated_at ? format(new Date(consolidatedData.generated_at), 'MMM d, yyyy h:mm a') : '';
    const header = `Consolidated Learnings\nScope: ${scope}\n${stamp ? 'Generated: ' + stamp + '\n' : ''}Based on ${consolidatedData.based_on_count} creative${consolidatedData.based_on_count === 1 ? '' : 's'}\n\n`;
    if (kind === 'clipboard') {
      const txt = header + pointers.map((p, i) => `${i + 1}. ${p}`).join('\n');
      navigator.clipboard.writeText(txt).then(
        () => toast.success('Copied to clipboard'),
        () => toast.error('Clipboard copy failed')
      );
      return;
    }
    if (kind === 'txt') {
      const txt = header + pointers.map((p, i) => `${i + 1}. ${p}`).join('\n');
      const blob = new Blob([txt], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consolidated-learnings-${new Date().toISOString().split('T')[0]}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded .txt');
      return;
    }
    if (kind === 'csv') {
      const rows = [['#', 'Pointer'], ...pointers.map((p, i) => [String(i + 1), p])];
      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consolidated-learnings-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded .csv');
    }
  };


  const saveAdName = async (id, newName, currentMetrics) => {
    const trimmed = (newName || '').trim();
    const merged = { ...(currentMetrics || {}), ad_name: trimmed };
    try {
      const response = await fetch(`${API_URL}/api/creative-analyses/${id}/metrics`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(merged)
      });
      if (response.ok) {
        setHistory(prev => prev.map(item => item.id === id ? { ...item, metrics: merged } : item));
        if (selectedCreative?.id === id) {
          setSelectedCreative(prev => ({ ...prev, metrics: merged }));
        }
        toast.success('Ad name saved');
      }
    } catch (e) {
      toast.error('Failed to save ad name');
    }
  };

  // Filter dropdown options come from a dedicated endpoint so the option
  // list stays stable regardless of which page/filter is currently loaded.
  const themes = allThemes.length ? allThemes : [...new Set(history.map(h => h.analysis?.theme).filter(Boolean))];
  const usedBrands = allUsedBrands.length ? allUsedBrands : [...new Set(history.map(h => h.brand).filter(Boolean))];

  // All filters (search/brand/theme/competitor/roas/media_type) are applied
  // server-side, so the payload already contains only matching creatives.
  // We just sort by ROAS for consistent ordering.
  const filteredHistory = [...history].sort((a, b) => (b.metrics?.roas || 0) - (a.metrics?.roas || 0));

  const getRoasBadge = (roas) => {
    if (!roas) return null;
    if (roas >= roasThreshold) return <Badge className="bg-emerald-500 text-white">{roas.toFixed(1)}x</Badge>;
    if (roas >= roasThreshold * 0.67) return <Badge className="bg-amber-500 text-white">{roas.toFixed(1)}x</Badge>;
    return <Badge className="bg-red-500 text-white">{roas.toFixed(1)}x</Badge>;
  };

  // Helper to render text with **bold** markdown
  const renderBoldText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-bold text-slate-900">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const CreativeCard = ({ item }) => {
    const analysis = item.analysis || {};
    const metrics = item.metrics || {};
    const isSelected = selectedIds.includes(item.id);
    const isTopPerformer = metrics.roas && metrics.roas >= roasThreshold;
    const brandColor = BRAND_COLORS[item.brand] || BRAND_COLORS.default;
    
    return (
      <div 
        className={`bg-white rounded-xl border overflow-hidden transition-all group relative ${
          isSelected ? 'ring-2 ring-violet-500 border-violet-500' : 'border-slate-200 hover:shadow-lg'
        }`}
        style={isTopPerformer ? { 
          borderColor: brandColor, 
          boxShadow: `0 0 0 2px ${brandColor}40`
        } : {}}
      >
        {/* Top Performer Badge */}
        {isTopPerformer && (
          <div 
            className="absolute top-0 left-0 right-0 text-white text-[10px] font-bold text-center py-0.5 z-20"
            style={{ backgroundColor: brandColor }}
          >
            ⭐ TOP PERFORMER
          </div>
        )}

        {/* Selection checkbox */}
        <div 
          className={`absolute ${isTopPerformer ? 'top-6' : 'top-2'} left-2 z-10 transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
          onClick={(e) => toggleSelect(item.id, e)}
        >
          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center cursor-pointer ${
            isSelected ? 'bg-violet-500 border-violet-500' : 'bg-white/90 border-slate-300'
          }`}>
            {isSelected && <Check className="h-4 w-4 text-white" />}
          </div>
        </div>

        {/* ROAS Badge */}
        {metrics.roas && (
          <div className={`absolute ${isTopPerformer ? 'top-6' : 'top-2'} right-2 z-10`}>
            {getRoasBadge(metrics.roas)}
          </div>
        )}

        {/* Edit metrics button */}
        <button
          onClick={(e) => openMetricsEditor(item, e)}
          className={`absolute ${isTopPerformer ? 'top-6' : 'top-2'} right-2 z-10 w-7 h-7 rounded-full bg-white/90 hover:bg-blue-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm border`}
          style={{ right: metrics.roas ? '48px' : '8px' }}
        >
          <Edit3 className="h-3.5 w-3.5 text-blue-500" />
        </button>

        {/* Delete button */}
        <button
          onClick={(e) => deleteCreative(item.id, e)}
          className="absolute bottom-14 right-2 z-10 w-7 h-7 rounded-full bg-white/90 hover:bg-red-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm border"
        >
          {deleting === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-red-500" />}
        </button>
        
        {/* Image/Video */}
        <div className="relative aspect-[4/3] bg-slate-100 cursor-pointer" onClick={() => !item.is_video && openCreativeDetail(item)}>
          <LazyMedia 
            key={`${item.id}-${item.thumbnail_frame_index ?? 'default'}`}
            itemId={item.id} 
            filename={item.filename} 
            contentType={item.content_type} 
            isVideo={item.is_video}
            onViewDetails={() => openCreativeDetail(item)}
          />
          {!item.is_video && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100"><Eye className="h-4 w-4 mr-1" /> View</Button>
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-3 cursor-pointer" onClick={() => openCreativeDetail(item)}>
          <h3 className="font-medium text-sm text-slate-900 truncate hover:text-violet-600 transition-colors">
            {analysis.headline && analysis.headline !== 'Not visible' ? analysis.headline : item.filename}
          </h3>
          
          {/* Tags */}
          <div className="flex flex-wrap gap-1 mt-2">
            {item.brand && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-medium">{item.brand}</span>}
            {item.competitor && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-medium">{item.competitor}</span>}
            {analysis.theme && <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-medium">{analysis.theme}</span>}
            {item.learning && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium" title={item.learning} data-testid={`creative-card-learning-badge-${item.id}`}>
                <Lightbulb className="h-2.5 w-2.5" /> Learning
              </span>
            )}
          </div>

          {/* Metrics row */}
          {(metrics.spend || metrics.roas) && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100 text-[10px]">
              {metrics.spend && <span className="text-slate-600">₹{Number(metrics.spend).toLocaleString()}</span>}
              {metrics.audience && <span className="text-slate-400 truncate max-w-[80px]">{metrics.audience}</span>}
            </div>
          )}
          <MetaAccountChips metrics={metrics} />
          <MetaRunInfo metrics={metrics} />

          {/* Date */}
          <div className="flex items-center justify-between mt-2 text-[10px] text-slate-400">
            <span>{item.created_at ? format(new Date(item.created_at), 'MMM d') : '—'}</span>
            {!metrics.roas && <span className="text-amber-500">No metrics</span>}
          </div>
        </div>
      </div>
    );
  };

  // Detailed List Item Component - Responsive
  const CreativeListItem = ({ item }) => {
    const analysis = item.analysis || {};
    const metrics = item.metrics || {};
    const isSelected = selectedIds.includes(item.id);
    const isTopPerformer = metrics.roas && metrics.roas >= roasThreshold;
    const brandColor = BRAND_COLORS[item.brand] || BRAND_COLORS.default;
    
    return (
      <div 
        className={`bg-white px-4 py-3 flex items-center gap-3 transition-all hover:bg-slate-50 ${
          isSelected ? 'bg-violet-50' : ''
        }`}
        style={isTopPerformer ? { borderLeftWidth: '4px', borderLeftColor: brandColor } : {}}
      >
        {/* Selection checkbox */}
        <div className="flex-shrink-0 cursor-pointer" onClick={(e) => toggleSelect(item.id, e)}>
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
            isSelected ? 'bg-violet-500 border-violet-500' : 'border-slate-300 hover:border-violet-400'
          }`}>
            {isSelected && <Check className="h-3 w-3 text-white" />}
          </div>
        </div>
        
        {/* Thumbnail */}
        <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-slate-100 cursor-pointer relative" onClick={() => openCreativeDetail(item)}>
          <LazyMedia 
            key={`${item.id}-${item.thumbnail_frame_index ?? 'default'}`}
            itemId={item.id} 
            filename={item.filename} 
            contentType={item.content_type} 
            isVideo={item.is_video}
            onViewDetails={() => openCreativeDetail(item)}
          />
          {item.is_video && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Film className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
        
        {/* Creative Name */}
        <div className="w-36 flex-shrink-0 overflow-hidden">
          <div className="flex items-center gap-1">
            <h3 className="font-medium text-xs text-slate-900 truncate" title={item.filename}>
              {item.filename}
            </h3>
            {isTopPerformer && (
              <span className="px-1 py-0.5 rounded text-[8px] font-bold text-white flex-shrink-0" style={{ backgroundColor: brandColor }}>
                TOP
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            {item.brand && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-medium">{item.brand}</span>}
            {item.competitor && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-medium">{item.competitor}</span>}
          </div>
        </div>
        
        {/* Ad Name - Flexible */}
        <div className="flex-1 min-w-[120px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <AdNameInlineEditor item={item} onSave={saveAdName} />
          <MetaAccountChips metrics={metrics} />
          <MetaRunInfo metrics={metrics} />
        </div>
        
        {/* ROAS */}
        <div className="w-14 text-center flex-shrink-0">
          {metrics.roas ? (
            <span className={`font-bold text-sm ${metrics.roas >= roasThreshold ? 'text-emerald-600' : metrics.roas >= roasThreshold * 0.67 ? 'text-amber-600' : 'text-red-500'}`}>
              {metrics.roas}x
            </span>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </div>
        
        {/* Spend */}
        <div className="w-20 text-center flex-shrink-0">
          {metrics.spend ? (
            <span className="font-medium text-xs text-slate-700">₹{Number(metrics.spend).toLocaleString()}</span>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </div>
        
        {/* Audience */}
        <div className="w-28 flex-shrink-0 overflow-hidden">
          {metrics.audience ? (
            <span className="text-xs text-slate-600 block" title={metrics.audience}>{metrics.audience}</span>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </div>
        
        {/* Age */}
        <div className="w-16 text-center flex-shrink-0">
          {metrics.age_group?.length > 0 ? (
            <span className="text-[10px] text-slate-600 block" title={Array.isArray(metrics.age_group) ? metrics.age_group.join(', ') : metrics.age_group}>
              {Array.isArray(metrics.age_group) ? metrics.age_group[0] : metrics.age_group}
              {Array.isArray(metrics.age_group) && metrics.age_group.length > 1 && <span className="text-slate-400 ml-0.5">+{metrics.age_group.length - 1}</span>}
            </span>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </div>
        
        {/* Location */}
        <div className="w-16 text-center flex-shrink-0">
          {metrics.location?.length > 0 ? (
            <span className="text-[10px] text-slate-600 block" title={Array.isArray(metrics.location) ? metrics.location.join(', ') : metrics.location}>
              {Array.isArray(metrics.location) ? metrics.location[0] : metrics.location}
              {Array.isArray(metrics.location) && metrics.location.length > 1 && <span className="text-slate-400 ml-0.5">+{metrics.location.length - 1}</span>}
            </span>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </div>

        {/* Learning */}
        <div className="w-44 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <LearningInlineEditor itemId={item.id} value={item.learning} onSave={saveLearning} compact />
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-0.5 flex-shrink-0 w-20 justify-center">
          <Button size="sm" variant="ghost" onClick={() => openCreativeDetail(item)} className="h-7 w-7 p-0" title="View Details">
            <Eye className="h-3.5 w-3.5 text-slate-500" />
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => openMetricsEditor(item, e)} className="h-7 w-7 p-0" title="Edit Metrics">
            <Edit3 className="h-3.5 w-3.5 text-blue-500" />
          </Button>
          <Button size="sm" variant="ghost" onClick={(e) => deleteCreative(item.id, e)} className="h-7 w-7 p-0" title="Delete">
            {deleting === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-red-400" />}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="creative-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <FileImage className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              Creative Log
              {totalItems > 0 && (
                <span className="relative">
                  <span className="animate-ping absolute inset-0 rounded-full bg-violet-400 opacity-75"></span>
                  <span className="relative inline-flex items-center justify-center h-6 min-w-[24px] px-2 rounded-full bg-violet-500 text-white text-xs font-bold">{totalItems}</span>
                </span>
              )}
            </h1>
            {stats && stats.total_analyses > 0 && (
              <div className="flex items-center gap-3 text-xs mt-0.5">
                <span className="text-slate-500"><span className="font-semibold text-amber-600">₹{toINR(stats.total_cost_usd)}</span> spent</span>
                <span className="text-slate-300">•</span>
                <span className="text-slate-500">{stats.total_tokens?.toLocaleString()} tokens</span>
                <button onClick={() => setShowCostDetails(!showCostDetails)} className="text-violet-500 hover:text-violet-600 font-medium ml-1">
                  {showCostDetails ? 'Less' : 'Details'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Consolidated Learnings Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={openConsolidated}
            className="h-8 px-3 gap-2 border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 hover:text-amber-800"
            data-testid="open-consolidated-learnings-btn"
          >
            <Lightbulb className="h-4 w-4" />
            Consolidated Learnings
          </Button>

          {/* CSV Import Button */}
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => setShowCsvImport(!showCsvImport)}
            className="h-8 px-3 gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Import Metrics
          </Button>

          {/* Meta ROAS Sync — fetches live spend/CTR/ROAS/CVR/CPA from Meta
              Ads Insights and fuzzy-matches by ad_name onto this log. Admin
              only; disabled with a helpful toast if the token isn't set. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => syncRoasFromMeta(selectedIds)}
            disabled={syncingRoas}
            title={selectedIds.length ? 'Sync only the selected creatives' : 'Sync all creatives (lifetime)'}
            className="h-8 px-3 gap-2"
            data-testid="sync-roas-btn"
          >
            {syncingRoas
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <RefreshCw className="h-4 w-4" />
            }
            Sync ROAS{selectedIds.length > 0 && ` (${selectedIds.length})`}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={refreshLiveStatus}
            disabled={refreshingStatus}
            className="h-8 px-3 gap-2"
            data-testid="refresh-live-status-btn"
            title="Quickly re-check which ads are live on Meta (no ROAS refresh)"
          >
            {refreshingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="h-2 w-2 rounded-full bg-emerald-500" />}
            Live Status{selectedIds.length > 0 && ` (${Math.min(selectedIds.length, 25)})`}
          </Button>
          
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-2 mr-4 px-3 py-1.5 bg-violet-50 rounded-lg">
              <span className="text-sm font-medium text-violet-700">{selectedIds.length} selected</span>
              <div className="w-px h-5 bg-violet-200"></div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="h-7 px-2"><X className="h-4 w-4" /></Button>
              <Button size="sm" onClick={bulkDelete} disabled={bulkDeleting} className="h-7 bg-red-500 hover:bg-red-600 text-white">
                {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            </div>
          )}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <Button variant={viewMode === 'grid' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className="h-8 px-3" title="Large Cards" data-testid="view-mode-grid-btn">
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button variant={viewMode === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className="h-8 px-3" title="Detailed List" data-testid="view-mode-list-btn">
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* CSV Import Panel */}
      {showCsvImport && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-bold text-blue-900 flex items-center gap-2 mb-2">
                <FileSpreadsheet className="h-4 w-4" />
                Bulk Import Performance Metrics
              </h3>
              <p className="text-xs text-slate-600 mb-4">
                Upload a CSV file to import metrics for multiple creatives at once. The <strong>creative_id</strong> column must match the exact filename of uploaded creatives.
              </p>
              
              {/* CSV Format Info */}
              {!csvPreview && (
                <div className="bg-white rounded-lg p-3 border border-blue-100 mb-4">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Required CSV Headers</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['creative_id', 'ad_name', 'location', 'age_group', 'gender', 'spend', 'roas', 'audience', 'campaign_start_date'].map(header => (
                      <span key={header} className={`px-2 py-0.5 rounded text-xs ${header === 'creative_id' ? 'bg-blue-100 text-blue-700 font-semibold' : 'bg-slate-100 text-slate-600'}`}>
                        {header}{header === 'creative_id' && ' *'}
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">* Required field. Multiple values for location/age_group can be comma-separated.</p>
                </div>
              )}
              
              {/* Upload Actions - Show only when no preview */}
              {!csvPreview && (
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".csv,.xlsx,.xls" 
                      onChange={handleCsvPreview}
                      className="hidden"
                      disabled={csvUploading}
                    />
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      csvUploading 
                        ? 'bg-slate-200 text-slate-500 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                    }`}>
                      {csvUploading ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing...</>
                      ) : (
                        <><Upload className="h-4 w-4" /> Upload CSV</>
                      )}
                    </div>
                  </label>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={downloadCsvTemplate}
                    className="h-9 gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Template
                  </Button>
                </div>
              )}
            </div>
            
            <Button variant="ghost" size="sm" onClick={() => { setShowCsvImport(false); setCsvResult(null); setCsvPreview(null); setCsvFile(null); }} className="h-8 px-2">
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Preview Table */}
          {csvPreview && csvPreview.success && (
            <div className="mt-4 space-y-4">
              {/* Preview Summary */}
              <div className="flex items-center justify-between bg-white rounded-lg p-4 border">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{csvPreview.total_rows}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Total Rows</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-600">{csvPreview.matched_count}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Matched</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{csvPreview.unmatched_count}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Unmatched</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={cancelCsvPreview}
                    className="h-9"
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={confirmCsvImport}
                    disabled={importingAfterPreview || csvPreview.matched_count === 0}
                    className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  >
                    {importingAfterPreview ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Importing...</>
                    ) : (
                      <><Check className="h-4 w-4" /> Confirm Import ({csvPreview.matched_count})</>
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Matched Creatives Table */}
              {csvPreview.matched?.length > 0 && (
                <div className="bg-emerald-50 rounded-lg border border-emerald-200 overflow-hidden">
                  <div className="px-4 py-2 bg-emerald-100 border-b border-emerald-200">
                    <h4 className="text-xs font-bold text-emerald-800 uppercase flex items-center gap-2">
                      <Check className="h-3 w-3" /> Matched Creatives ({csvPreview.matched_count})
                    </h4>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-emerald-100/50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-emerald-700 font-semibold">Creative ID</th>
                          <th className="px-3 py-2 text-left text-emerald-700 font-semibold">Brand</th>
                          <th className="px-3 py-2 text-left text-emerald-700 font-semibold">Theme</th>
                          <th className="px-3 py-2 text-right text-emerald-700 font-semibold">ROAS</th>
                          <th className="px-3 py-2 text-right text-emerald-700 font-semibold">Spend</th>
                          <th className="px-3 py-2 text-left text-emerald-700 font-semibold">Audience</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-emerald-100">
                        {csvPreview.matched.slice(0, 20).map((item, idx) => (
                          <tr key={idx} className="hover:bg-emerald-100/30">
                            <td className="px-3 py-2 font-medium text-slate-700">{item.creative_id}</td>
                            <td className="px-3 py-2 text-slate-600">{item.brand}</td>
                            <td className="px-3 py-2 text-slate-600">{item.theme}</td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-700">{item.metrics.roas ? `${item.metrics.roas}x` : '—'}</td>
                            <td className="px-3 py-2 text-right text-slate-600">{item.metrics.spend ? `₹${item.metrics.spend.toLocaleString()}` : '—'}</td>
                            <td className="px-3 py-2 text-slate-600">{item.metrics.audience || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvPreview.matched.length > 20 && (
                      <p className="px-3 py-2 text-[10px] text-emerald-600 bg-emerald-100/50">
                        +{csvPreview.matched.length - 20} more rows...
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Unmatched Creatives Table */}
              {csvPreview.unmatched?.length > 0 && (
                <div className="bg-red-50 rounded-lg border border-red-200 overflow-hidden">
                  <div className="px-4 py-2 bg-red-100 border-b border-red-200">
                    <h4 className="text-xs font-bold text-red-800 uppercase flex items-center gap-2">
                      <X className="h-3 w-3" /> Unmatched — Will Be Skipped ({csvPreview.unmatched_count})
                    </h4>
                  </div>
                  <div className="max-h-32 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-red-100/50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-red-700 font-semibold">Row</th>
                          <th className="px-3 py-2 text-left text-red-700 font-semibold">Creative ID</th>
                          <th className="px-3 py-2 text-left text-red-700 font-semibold">Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-red-100">
                        {csvPreview.unmatched.slice(0, 10).map((item, idx) => (
                          <tr key={idx} className="hover:bg-red-100/30">
                            <td className="px-3 py-2 text-slate-500">{item.row}</td>
                            <td className="px-3 py-2 font-medium text-slate-700">{item.creative_id}</td>
                            <td className="px-3 py-2 text-red-600">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvPreview.unmatched.length > 10 && (
                      <p className="px-3 py-2 text-[10px] text-red-600 bg-red-100/50">
                        +{csvPreview.unmatched.length - 10} more rows...
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Import Result */}
          {csvResult && (
            <div className={`mt-4 p-4 rounded-lg border ${csvResult.updated_count > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-start gap-3">
                {csvResult.updated_count > 0 ? (
                  <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${csvResult.updated_count > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {csvResult.updated_count > 0 
                      ? `Successfully updated ${csvResult.updated_count} creative(s)` 
                      : 'Import completed with issues'}
                  </p>
                  
                  {csvResult.not_found_count > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-600 mb-1">{csvResult.not_found_count} creative ID(s) not found:</p>
                      <div className="flex flex-wrap gap-1">
                        {csvResult.not_found?.slice(0, 10).map((id, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-white rounded text-xs text-slate-600 border">{id}</span>
                        ))}
                        {csvResult.not_found?.length > 10 && (
                          <span className="px-2 py-0.5 text-xs text-slate-500">+{csvResult.not_found.length - 10} more</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {csvResult.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-red-600">{csvResult.errors.length} error(s):</p>
                      <ul className="text-xs text-red-500 mt-1 space-y-0.5">
                        {csvResult.errors.slice(0, 3).map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cost Details Expandable */}
      {showCostDetails && stats && (
        <div className="bg-slate-50 rounded-xl border p-4 animate-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-4 gap-6 text-sm">
            <div><p className="text-slate-400 text-xs mb-1">Total Spent</p><p className="font-bold text-lg text-amber-600">₹{toINR(stats.total_cost_usd)}</p></div>
            <div><p className="text-slate-400 text-xs mb-1">Tokens Used</p><p className="font-bold text-lg text-slate-700">{stats.total_tokens?.toLocaleString()}</p></div>
            <div><p className="text-slate-400 text-xs mb-1">Avg per Analysis</p><p className="font-bold text-lg text-emerald-600">₹{toINR(stats.avg_cost_per_analysis)}</p></div>
            <div><p className="text-slate-400 text-xs mb-1">Exchange Rate</p><p className="font-bold text-lg text-slate-700">₹{usdToInr.toFixed(2)}</p></div>
          </div>
        </div>
      )}

      {/* AI Insights Section - Clean Apple-like Design */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="insights-panel">
        {/* Minimal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center">
              <Brain className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Performance Intelligence</h3>
              <p className="text-xs text-slate-400">
                {insights ? (
                  <>
                    {insights.statistical_insights?.total_creatives || 0} creatives • 
                    <span className="font-semibold text-slate-600" title="Last analysis performed at this time">
                      🕐 {formatDateIST(insights.analyzed_at)}
                    </span>
                  </>
                ) : selectedIds.length > 0 ? (
                  <span className="text-violet-600 font-medium">{selectedIds.length} creatives selected for analysis</span>
                ) : 'Select creatives or analyze all'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* History Dropdown */}
            {analysisHistory.length > 0 && (
              <div className="relative">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                  className="h-8 px-2"
                  title="View past analyses"
                >
                  <History className="h-4 w-4" />
                </Button>
                {showHistoryDropdown && (
                  <div className="absolute right-0 top-10 w-64 bg-white rounded-lg shadow-lg border z-50 py-2">
                    <p className="px-3 py-1 text-[10px] font-medium text-slate-400 uppercase">Past Analyses (Last 5)</p>
                    {analysisHistory.map((h, idx) => (
                      <button
                        key={idx}
                        onClick={() => loadHistoricalAnalysis(h)}
                        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between"
                      >
                        <div>
                          <p className="text-sm text-slate-700">
                            {formatDateIST(h.created_at)}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {h.brand_filter !== 'all' ? h.brand_filter : 'All Brands'} • ROAS ≥{h.roas_threshold}x
                          </p>
                        </div>
                        <span className="text-[10px] text-emerald-600">{h.statistical_insights?.high_performers || 0} top</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Threshold with visible hint */}
            <div className="flex items-center gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-slate-500">
                <span>ROAS ≥</span>
                <Input 
                  type="number" 
                  step="0.5" 
                  min="1" 
                  max="10"
                  value={roasThreshold} 
                  onChange={(e) => setRoasThreshold(parseFloat(e.target.value) || 3)}
                  className="w-20 h-8 text-sm text-center px-2"
                />
              </div>
              <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-1 rounded">= Top Performer</span>
            </div>

            {/* Animated Analyze Button */}
            <Button 
              size="sm" 
              onClick={() => fetchInsights()}
              disabled={insightsLoading}
              className={`h-8 px-4 transition-all ${insightsLoading 
                ? 'bg-gradient-to-r from-violet-600 via-purple-600 to-violet-600 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]' 
                : 'bg-slate-900 hover:bg-slate-800'} text-white`}
              title={selectedIds.length > 0 ? `Analyze ${selectedIds.length} selected creatives` : 'Analyze all creatives'}
            >
              {insightsLoading ? (
                <>
                  <svg className="h-4 w-4 mr-1.5 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h-1.5M12 2a2 2 0 00-2 2c0 .74.4 1.39 1 1.73V7h-1a7 7 0 00-7 7h1.5M9 22h6M12 17v5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="animate-pulse">{insightsProgress}%</span>
                </>
              ) : (
                <>
                  <Brain className="h-4 w-4 mr-1.5" />
                  {selectedIds.length > 0 ? `Analyze (${selectedIds.length})` : 'Analyze All'}
                </>
              )}
            </Button>

            {insights && (
              <button onClick={() => setInsightsExpanded(!insightsExpanded)} className="p-1 hover:bg-slate-100 rounded">
                {insightsExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </button>
            )}
          </div>
        </div>

        {/* Progress Visualization */}
        {insightsLoading && (
          <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-indigo-50">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-violet-700">{insightsStep}</span>
                  <span className="text-sm font-bold text-violet-600">{insightsProgress}%</span>
                </div>
                <div className="h-2 bg-violet-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${insightsProgress}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <div className={`flex items-center gap-1.5 ${insightsProgress >= 30 ? 'text-emerald-600' : ''}`}>
                  <div className={`w-2 h-2 rounded-full ${insightsProgress >= 30 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  Data
                </div>
                <div className={`flex items-center gap-1.5 ${insightsProgress >= 60 ? 'text-emerald-600' : ''}`}>
                  <div className={`w-2 h-2 rounded-full ${insightsProgress >= 60 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  Patterns
                </div>
                <div className={`flex items-center gap-1.5 ${insightsProgress >= 85 ? 'text-emerald-600' : ''}`}>
                  <div className={`w-2 h-2 rounded-full ${insightsProgress >= 85 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  AI
                </div>
                <div className={`flex items-center gap-1.5 ${insightsProgress >= 100 ? 'text-emerald-600' : ''}`}>
                  <div className={`w-2 h-2 rounded-full ${insightsProgress >= 100 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                  Done
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {insightsError && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100">
            <p className="text-xs text-amber-700 flex items-center gap-2">
              <AlertTriangle className="h-3 w-3" /> {insightsError}
            </p>
          </div>
        )}

        {/* Expanded Content - Clean Grid Layout */}
        {insightsExpanded && insights && (
          <div className="p-6 space-y-6 animate-in fade-in duration-200">
            
            {/* Summary Bar */}
            {insights.ai_insights?.summary && (
              <div className="bg-slate-50 rounded-xl px-5 py-4">
                <p className="text-sm text-slate-700 leading-relaxed">{insights.ai_insights.summary}</p>
              </div>
            )}

            {/* Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-4">
                <p className="text-3xl font-light text-slate-900">{insights.statistical_insights?.total_creatives || 0}</p>
                <p className="text-[11px] text-slate-400 mt-1">Total Analyzed</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <p className="text-3xl font-light text-emerald-600">{insights.statistical_insights?.high_performers || 0}</p>
                <p className="text-[11px] text-emerald-600 mt-1">Top Performers</p>
              </div>
              <div className="text-center p-4">
                <p className="text-3xl font-light text-slate-900">{insights.statistical_insights?.medium_performers || 0}</p>
                <p className="text-[11px] text-slate-400 mt-1">Medium</p>
              </div>
              <div className="text-center p-4">
                <p className="text-3xl font-light text-slate-400">{insights.statistical_insights?.low_performers || 0}</p>
                <p className="text-[11px] text-slate-400 mt-1">Low</p>
              </div>
            </div>

            {insights.ai_insights && (
              <div className="space-y-5">
                {/* AI Summary Card */}
                {insights.ai_insights.detailed_summary && (
                  <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
                    <p className="text-sm text-slate-700 leading-relaxed">{renderBoldText(insights.ai_insights.detailed_summary)}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-5">
                  {/* Performing Elements Card */}
                  {insights.ai_insights.performing_elements?.length > 0 && (
                    <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                      <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px]">✓</span>
                        In Performing Creatives
                      </h4>
                      <ul className="space-y-2">
                        {insights.ai_insights.performing_elements.map((item, idx) => (
                          <li key={idx} className="text-sm text-slate-700 pl-3 border-l-2 border-emerald-300">{renderBoldText(item)}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Common in Both Card */}
                  {insights.ai_insights.common_in_both?.length > 0 && (
                    <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
                      <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-slate-400 text-white flex items-center justify-center text-[10px]">=</span>
                        In Both (Not Differentiators)
                      </h4>
                      <ul className="space-y-2">
                        {insights.ai_insights.common_in_both.map((item, idx) => (
                          <li key={idx} className="text-sm text-slate-600 pl-3 border-l-2 border-slate-300">{renderBoldText(item)}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Avoid These Card */}
                  {insights.ai_insights.avoid_these?.length > 0 && (
                    <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                      <h4 className="text-xs font-bold text-red-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px]">✗</span>
                        Avoid These
                      </h4>
                      <ul className="space-y-2">
                        {insights.ai_insights.avoid_these.map((item, idx) => (
                          <li key={idx} className="text-sm text-slate-700 pl-3 border-l-2 border-red-300">{renderBoldText(item)}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Winning Combinations Card */}
                  {insights.ai_insights.winning_combinations?.length > 0 && (
                    <div className="bg-violet-50 rounded-xl p-5 border border-violet-200">
                      <h4 className="text-xs font-bold text-violet-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-violet-500 text-white flex items-center justify-center text-[10px]">★</span>
                        Winning Combinations
                      </h4>
                      <ul className="space-y-2">
                        {insights.ai_insights.winning_combinations.map((item, idx) => (
                          <li key={idx} className="text-sm text-slate-700 pl-3 border-l-2 border-violet-300">{renderBoldText(item)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Creative Blueprint - Full Width To-Do Checklist */}
                {insights.ai_insights.creative_blueprint && (
                  <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-6 border border-indigo-200 mt-5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-bold text-indigo-900 uppercase tracking-wide flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-indigo-600" />
                        Creative Blueprint — Your To-Do Checklist
                      </h4>
                      {insights.ai_insights.roas_forecast && (
                        <div className="flex items-center gap-2 bg-emerald-100 px-3 py-1.5 rounded-full">
                          <Rocket className="h-4 w-4 text-emerald-600" />
                          <span className="text-sm font-bold text-emerald-700">
                            Expected ROAS: {insights.ai_insights.roas_forecast.expected_range}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Visual Style */}
                      {insights.ai_insights.creative_blueprint.visual_style && (
                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                          <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                          <div>
                            <p className="text-xs font-semibold text-indigo-800 uppercase">Visual Style</p>
                            <p className="text-sm text-slate-700 mt-0.5">{insights.ai_insights.creative_blueprint.visual_style}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Color Palette */}
                      {insights.ai_insights.creative_blueprint.color_palette?.length > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                          <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                          <div>
                            <p className="text-xs font-semibold text-indigo-800 uppercase">Color Palette</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {insights.ai_insights.creative_blueprint.color_palette.map((color, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">{color}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Imagery */}
                      {insights.ai_insights.creative_blueprint.imagery && (
                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                          <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                          <div>
                            <p className="text-xs font-semibold text-indigo-800 uppercase">Imagery</p>
                            <p className="text-sm text-slate-700 mt-0.5">{insights.ai_insights.creative_blueprint.imagery}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* CTA Button */}
                      {insights.ai_insights.creative_blueprint.cta_button && (
                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                          <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">4</div>
                          <div>
                            <p className="text-xs font-semibold text-indigo-800 uppercase">CTA Button</p>
                            <p className="text-sm text-slate-700 mt-0.5">{insights.ai_insights.creative_blueprint.cta_button}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Emotion */}
                      {insights.ai_insights.creative_blueprint.emotion && (
                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                          <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">5</div>
                          <div>
                            <p className="text-xs font-semibold text-indigo-800 uppercase">Emotional Tone</p>
                            <p className="text-sm text-slate-700 mt-0.5">{insights.ai_insights.creative_blueprint.emotion}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Layout */}
                      {insights.ai_insights.creative_blueprint.layout && (
                        <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-indigo-100">
                          <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">6</div>
                          <div>
                            <p className="text-xs font-semibold text-indigo-800 uppercase">Layout</p>
                            <p className="text-sm text-slate-700 mt-0.5">{insights.ai_insights.creative_blueprint.layout}</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Must Include & Must Avoid */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      {insights.ai_insights.creative_blueprint.must_include?.length > 0 && (
                        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <p className="text-xs font-bold text-emerald-700 uppercase mb-2 flex items-center gap-1">
                            <Check className="h-3 w-3" /> Must Include
                          </p>
                          <ul className="space-y-1">
                            {insights.ai_insights.creative_blueprint.must_include.map((item, idx) => (
                              <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {insights.ai_insights.creative_blueprint.must_avoid?.length > 0 && (
                        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-xs font-bold text-red-700 uppercase mb-2 flex items-center gap-1">
                            <X className="h-3 w-3" /> Must Avoid
                          </p>
                          <ul className="space-y-1">
                            {insights.ai_insights.creative_blueprint.must_avoid.map((item, idx) => (
                              <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Demographic Insights Card */}
                {insights.ai_insights.demographic_insights && (
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200 mt-5">
                    <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2 mb-4">
                      <Users className="h-5 w-5 text-blue-600" />
                      Demographic Insights — Who to Target
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Best Age Groups */}
                      {insights.ai_insights.demographic_insights.best_age_groups?.length > 0 && (
                        <div className="p-3 bg-white rounded-lg border border-blue-100">
                          <p className="text-[10px] font-bold text-blue-700 uppercase mb-2">Best Age Groups</p>
                          <div className="flex flex-wrap gap-1">
                            {insights.ai_insights.demographic_insights.best_age_groups.map((age, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{age}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Best Genders */}
                      {insights.ai_insights.demographic_insights.best_genders?.length > 0 && (
                        <div className="p-3 bg-white rounded-lg border border-blue-100">
                          <p className="text-[10px] font-bold text-blue-700 uppercase mb-2">Best Genders</p>
                          <div className="flex flex-wrap gap-1">
                            {insights.ai_insights.demographic_insights.best_genders.map((gender, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{gender}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Best Locations */}
                      {insights.ai_insights.demographic_insights.best_locations?.length > 0 && (
                        <div className="p-3 bg-white rounded-lg border border-blue-100">
                          <p className="text-[10px] font-bold text-blue-700 uppercase mb-2">Best Locations</p>
                          <div className="flex flex-wrap gap-1">
                            {insights.ai_insights.demographic_insights.best_locations.map((loc, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">{loc}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Demographics to Avoid */}
                      <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                        <p className="text-[10px] font-bold text-red-700 uppercase mb-2">Avoid These</p>
                        <div className="space-y-1">
                          {insights.ai_insights.demographic_insights.age_groups_to_avoid?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {insights.ai_insights.demographic_insights.age_groups_to_avoid.map((age, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-[10px]">{age}</span>
                              ))}
                            </div>
                          )}
                          {insights.ai_insights.demographic_insights.locations_to_avoid?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {insights.ai_insights.demographic_insights.locations_to_avoid.map((loc, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-[10px]">{loc}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Sweet Spot */}
                    {insights.ai_insights.demographic_insights.sweet_spot && (
                      <div className="mt-4 p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg text-white">
                        <p className="text-[10px] uppercase font-bold mb-1">🎯 Sweet Spot</p>
                        <p className="text-sm font-medium">{insights.ai_insights.demographic_insights.sweet_spot}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Audience Pointers Card */}
                {insights.ai_insights.audience_pointers && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mt-4">
                    <h4 className="text-sm font-bold text-purple-900 uppercase tracking-wide flex items-center gap-2 mb-4">
                      <Target className="h-5 w-5 text-purple-600" />
                      Audience Pointers — From Your Data
                    </h4>
                    
                    {/* Top Performing Audiences */}
                    {insights.ai_insights.audience_pointers.top_performing_audiences?.length > 0 && (
                      <div className="p-4 bg-white rounded-lg border-2 border-purple-200 mb-4">
                        <p className="text-[10px] font-bold text-purple-700 uppercase mb-2">Top Performing Audiences (High ROAS)</p>
                        <div className="flex flex-wrap gap-2">
                          {insights.ai_insights.audience_pointers.top_performing_audiences.map((aud, idx) => (
                            <span key={idx} className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium">{aud}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Primary Audience */}
                      {insights.ai_insights.audience_pointers.primary_audience && (
                        <div className="p-4 bg-white rounded-lg border border-purple-100">
                          <p className="text-[10px] font-bold text-purple-700 uppercase mb-1">Primary Audience</p>
                          <p className="text-sm text-slate-700 font-medium">{insights.ai_insights.audience_pointers.primary_audience}</p>
                        </div>
                      )}
                      
                      {/* Secondary Audience */}
                      {insights.ai_insights.audience_pointers.secondary_audience && (
                        <div className="p-4 bg-white rounded-lg border border-purple-100">
                          <p className="text-[10px] font-bold text-purple-600 uppercase mb-1">Secondary Audience</p>
                          <p className="text-sm text-slate-600">{insights.ai_insights.audience_pointers.secondary_audience}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Audiences to Avoid */}
                    {insights.ai_insights.audience_pointers.audiences_to_avoid?.length > 0 && (
                      <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-100">
                        <p className="text-[10px] font-bold text-red-700 uppercase mb-2">Audiences to Avoid (Low ROAS)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {insights.ai_insights.audience_pointers.audiences_to_avoid.map((aud, idx) => (
                            <span key={idx} className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs">{aud}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Targeting Recommendations */}
                    {insights.ai_insights.audience_pointers.targeting_recommendations?.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-[10px] font-bold text-purple-700 uppercase">Targeting Recommendations</p>
                        {insights.ai_insights.audience_pointers.targeting_recommendations.map((rec, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded-lg border border-purple-100">
                            <span className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{idx + 1}</span>
                            <p className="text-xs text-slate-700">{rec}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Budget Allocation */}
                    {insights.ai_insights.audience_pointers.budget_allocation && (
                      <div className="mt-4 p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white">
                        <p className="text-[10px] uppercase font-bold mb-1">💰 Budget Allocation</p>
                        <p className="text-sm font-medium">{insights.ai_insights.audience_pointers.budget_allocation}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* ROAS Forecast Card */}
                {insights.ai_insights.roas_forecast && (
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200 mt-4">
                    <h4 className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                      ROAS Forecast — If You Follow This Blueprint
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-white rounded-xl border border-emerald-100">
                        <p className="text-2xl font-bold text-emerald-600">{insights.ai_insights.roas_forecast.expected_range}</p>
                        <p className="text-[10px] text-slate-500 mt-1 uppercase">Expected Range</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-xl border border-emerald-100">
                        <p className="text-2xl font-bold text-emerald-700">{insights.ai_insights.roas_forecast.best_case}</p>
                        <p className="text-[10px] text-slate-500 mt-1 uppercase">Best Case</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-xl border border-emerald-100">
                        <p className="text-2xl font-bold text-amber-600">{insights.ai_insights.roas_forecast.minimum_expected}</p>
                        <p className="text-[10px] text-slate-500 mt-1 uppercase">Minimum Expected</p>
                      </div>
                    </div>
                    {insights.ai_insights.roas_forecast.conditions && (
                      <div className="mt-3 p-3 bg-white/60 rounded-lg">
                        <p className="text-xs text-slate-600">
                          <span className="font-semibold text-emerald-700">Key Condition:</span> {insights.ai_insights.roas_forecast.conditions}
                        </p>
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                        insights.ai_insights.roas_forecast.confidence === 'High' ? 'bg-emerald-100 text-emerald-700' :
                        insights.ai_insights.roas_forecast.confidence === 'Medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {insights.ai_insights.roas_forecast.confidence} Confidence
                      </span>
                    </div>
                  </div>
                )}

                {/* Campaign Tips Card */}
                {insights.ai_insights.campaign_tips?.length > 0 && (
                  <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 mt-2">
                    <h4 className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px]">💡</span>
                      Tips for Next Campaign
                    </h4>
                    <ul className="space-y-2">
                      {insights.ai_insights.campaign_tips.map((item, idx) => (
                        <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] flex-shrink-0 font-bold">{idx + 1}</span>
                          {renderBoldText(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <p className="text-[10px] text-slate-400">Powered by GPT-4o</p>
            </div>

            {/* Analyzed Creatives */}
            {insights.analyzed_creatives?.length > 0 && (
              <div className="bg-slate-50 rounded-xl p-4 mt-4">
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-3">
                  Creatives Included ({insights.analyzed_creatives.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {insights.analyzed_creatives.map((c, idx) => (
                    <span 
                      key={idx} 
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded-lg text-[10px] text-slate-600 hover:bg-slate-200 cursor-pointer"
                      onClick={() => {
                        const creative = history.find(h => h.id === c.id);
                        if (creative) openCreativeDetail(creative);
                      }}
                    >
                      <span className="max-w-[100px] truncate">{c.filename}</span>
                      {c.roas && <span className="text-emerald-600 font-medium">{c.roas.toFixed(1)}x</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {filteredHistory.length > 0 && (
              <Button variant="outline" size="sm" onClick={selectAll} className="h-9">
                <CheckSquare className="h-4 w-4 mr-2" />{selectedIds.length === filteredHistory.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search ad name, filename, headline..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-9" data-testid="creative-log-search" />
            </div>
            <Select value={filterBrand} onValueChange={setFilterBrand}>
              <SelectTrigger className="w-32 h-9"><SelectValue placeholder="Brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {usedBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterTheme} onValueChange={setFilterTheme}>
              <SelectTrigger className="w-32 h-9"><SelectValue placeholder="Theme" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Themes</SelectItem>
                {themes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterRoas} onValueChange={setFilterRoas}>
              <SelectTrigger className="w-36 h-9"><SelectValue placeholder="ROAS" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ROAS</SelectItem>
                <SelectItem value="high">🟢 High (3x+)</SelectItem>
                <SelectItem value="medium">🟡 Medium (2-3x)</SelectItem>
                <SelectItem value="low">🔴 Low (&lt;2x)</SelectItem>
                <SelectItem value="none">No metrics</SelectItem>
              </SelectContent>
            </Select>
            {/* Media Type Filter */}
            <Select value={filterMediaType} onValueChange={setFilterMediaType}>
              <SelectTrigger className="w-32 h-9"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="image">
                  <span className="flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> Images</span>
                </SelectItem>
                <SelectItem value="video">
                  <span className="flex items-center gap-1.5"><Film className="h-3.5 w-3.5" /> Videos</span>
                </SelectItem>
              </SelectContent>
            </Select>
            {metaAccounts.length > 0 && (
              <Select value={filterMetaAccount} onValueChange={setFilterMetaAccount}>
                <SelectTrigger className="w-40 h-9" data-testid="filter-meta-account"><SelectValue placeholder="Ad account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ad Accounts</SelectItem>
                  {metaAccounts.map((a) => <SelectItem key={a.account_id} value={a.account_id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {metaAccounts.length > 0 && (
              <Select value={filterLive} onValueChange={setFilterLive}>
                <SelectTrigger className="w-32 h-9" data-testid="filter-live-status"><SelectValue placeholder="Live status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Status</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="not_live">Not live</SelectItem>
                </SelectContent>
              </Select>
            )}
            {/* Date-range filter — filters `created_at` (upload date). */}
            <DateRangeFilter
              value={dateRange}
              onApply={setDateRange}
              resultCount={totalItems}
              buttonTestId="creative-log-date-range"
            />
          </div>
        </CardContent>
      </Card>

      {/* Grid / List View */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-500" /></div>
      ) : filteredHistory.length === 0 ? (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="py-20 text-center">
            <FileImage className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-1">No creatives found</h3>
            <p className="text-sm text-slate-500">Upload and analyze creatives to see them here</p>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        /* Detailed List View - Responsive */
        <div className="space-y-0" data-testid="creative-list-view">
          {/* List Header - Brand Color #2eb4ac */}
          <div className="rounded-t-lg px-4 py-2.5 flex items-center gap-3 text-xs font-semibold text-white uppercase tracking-wide" style={{ backgroundColor: '#2eb4ac' }}>
            <div className="w-6"></div>
            <div className="w-12 text-center">Preview</div>
            <div className="w-36">Creative</div>
            <div className="flex-1 min-w-[120px]">Ad Name</div>
            <div className="w-14 text-center">ROAS</div>
            <div className="w-20 text-center">Spend</div>
            <div className="w-28">Audience</div>
            <div className="w-16 text-center">Age</div>
            <div className="w-16 text-center">Location</div>
            <div className="w-44">Learning</div>
            <div className="w-20 text-center">Actions</div>
          </div>
          {/* List Items */}
          <div className="divide-y divide-slate-100 border border-t-0 border-slate-200 rounded-b-lg overflow-hidden">
            {filteredHistory.map(item => <CreativeListItem key={item.id} item={item} />)}
          </div>
          
          {/* Pagination Controls */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between mt-4 mb-16 px-4 py-3 bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">Show</span>
                <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-600">per page</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">
                  {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                </span>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="h-8 px-2"
                  >
                    «
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="h-8 px-3"
                  >
                    Prev
                  </Button>
                  <span className="px-3 py-1 bg-slate-100 rounded text-sm font-medium">
                    {currentPage} / {Math.max(1, Math.ceil(totalItems / itemsPerPage))}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.min(Math.max(1, Math.ceil(totalItems / itemsPerPage)), p + 1))}
                    disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                    className="h-8 px-3"
                  >
                    Next
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(Math.max(1, Math.ceil(totalItems / itemsPerPage)))}
                    disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                    className="h-8 px-2"
                  >
                    »
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Grid View (Large Cards) with Pagination */
        <div className="space-y-4 pb-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="creative-grid-view">
            {filteredHistory.map(item => <CreativeCard key={item.id} item={item} />)}
          </div>
          
          {/* Pagination Controls for Grid View */}
          {totalItems > 0 && (
            <div className="flex items-center justify-between mt-4 px-2 py-3 bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">Show</span>
                <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-20 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-600">per page</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">
                  {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                </span>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="h-8 px-2"
                  >
                    «
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="h-8 px-3"
                  >
                    Prev
                  </Button>
                  <span className="px-3 py-1 bg-slate-100 rounded text-sm font-medium">
                    {currentPage} / {Math.max(1, Math.ceil(totalItems / itemsPerPage))}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(p => Math.min(Math.max(1, Math.ceil(totalItems / itemsPerPage)), p + 1))}
                    disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                    className="h-8 px-3"
                  >
                    Next
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setCurrentPage(Math.max(1, Math.ceil(totalItems / itemsPerPage)))}
                    disabled={currentPage >= Math.ceil(totalItems / itemsPerPage)}
                    className="h-8 px-2"
                  >
                    »
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <CreativeStructuredDetail
        creative={selectedCreative}
        apiUrl={API_URL}
        onClose={() => setSelectedCreative(null)}
        onEditMetrics={(e) => openMetricsEditor(selectedCreative, e)}
        onDelete={(e) => deleteCreative(selectedCreative.id, e)}
        onSaveAdName={(name) => saveAdName(selectedCreative.id, name, selectedCreative.metrics)}
        onSaveLearning={(text) => saveLearning(selectedCreative.id, text)}
        onRephraseLearning={() => rephraseLearning(selectedCreative.id)}
        rephrasingLearning={rephrasingLearning}
        onReanalyze={() => reanalyzeCreative(selectedCreative.id)}
        reanalyzing={reanalyzingId === selectedCreative?.id}
        onGenerateLearningAnalysis={(force) => generateLearningAnalysis(selectedCreative.id, force)}
        learningAnalysisLoading={learningAnalysisLoadingId === selectedCreative?.id}
      />

      {/* Metrics Editor Modal */}
      <Dialog open={!!editingMetrics} onOpenChange={() => setEditingMetrics(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-violet-500" /> Performance Metrics</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">ROAS</Label>
                <Input 
                  type="number" 
                  step="0.1" 
                  placeholder="e.g. 2.5" 
                  defaultValue={metricsForm.roas || ''} 
                  onBlur={(e) => setMetricsForm(prev => ({ ...prev, roas: parseFloat(e.target.value) || null }))} 
                />
              </div>
              <div>
                <Label className="text-xs">Spend (₹)</Label>
                <Input 
                  type="number" 
                  placeholder="e.g. 50000" 
                  defaultValue={metricsForm.spend || ''} 
                  onBlur={(e) => setMetricsForm(prev => ({ ...prev, spend: parseFloat(e.target.value) || null }))} 
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Audience Targeted</Label>
              <Input 
                placeholder="e.g. Health & Fitness, Gym Enthusiasts" 
                defaultValue={metricsForm.audience || ''} 
                onBlur={(e) => setMetricsForm(prev => ({ ...prev, audience: e.target.value }))} 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Age Group</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {AGE_GROUPS.map(age => (
                    <Badge key={age} variant={metricsForm.age_group?.includes(age) ? 'default' : 'outline'} className="cursor-pointer"
                      onClick={() => setMetricsForm(prev => ({
                        ...prev,
                        age_group: prev.age_group?.includes(age) 
                          ? prev.age_group.filter(a => a !== age)
                          : [...(prev.age_group || []), age]
                      }))}>
                      {age}
                    </Badge>
                  ))}
                </div>
                {/* Show custom age groups from CSV */}
                {metricsForm.age_group?.filter(ag => !AGE_GROUPS.includes(ag)).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {metricsForm.age_group.filter(ag => !AGE_GROUPS.includes(ag)).map(customAge => (
                      <Badge key={customAge} variant="default" className="cursor-pointer bg-blue-500"
                        onClick={() => setMetricsForm(prev => ({
                          ...prev,
                          age_group: prev.age_group.filter(a => a !== customAge)
                        }))}>
                        {customAge} ×
                      </Badge>
                    ))}
                  </div>
                )}
                <Input 
                  placeholder="Or type custom (e.g. 18-32)" 
                  className="mt-2 h-8 text-xs"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      setMetricsForm(prev => ({
                        ...prev,
                        age_group: [...(prev.age_group || []), e.target.value.trim()]
                      }));
                      e.target.value = '';
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-xs">Gender</Label>
                <Select value={metricsForm.gender || ''} onValueChange={(v) => setMetricsForm(prev => ({ ...prev, gender: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {LOCATIONS.map(loc => (
                  <Badge key={loc} variant={metricsForm.location?.includes(loc) ? 'default' : 'outline'} className="cursor-pointer text-xs"
                    onClick={() => setMetricsForm(prev => ({
                      ...prev,
                      location: prev.location?.includes(loc) 
                        ? prev.location.filter(l => l !== loc)
                        : [...(prev.location || []), loc]
                    }))}>
                    {loc}
                  </Badge>
                ))}
              </div>
              {/* Show custom locations from CSV */}
              {metricsForm.location?.filter(loc => !LOCATIONS.includes(loc)).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {metricsForm.location.filter(loc => !LOCATIONS.includes(loc)).map(customLoc => (
                    <Badge key={customLoc} variant="default" className="cursor-pointer bg-blue-500 text-xs"
                      onClick={() => setMetricsForm(prev => ({
                        ...prev,
                        location: prev.location.filter(l => l !== customLoc)
                      }))}>
                      {customLoc} ×
                    </Badge>
                  ))}
                </div>
              )}
              <Input 
                placeholder="Or type custom (e.g. IN, US, Delhi)" 
                className="mt-2 h-8 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    setMetricsForm(prev => ({
                      ...prev,
                      location: [...(prev.location || []), e.target.value.trim()]
                    }));
                    e.target.value = '';
                  }
                }}
              />
            </div>
            <div>
              <Label className="text-xs">Campaign Start Date</Label>
              <Input 
                type="date" 
                defaultValue={metricsForm.campaign_start_date || ''} 
                onBlur={(e) => setMetricsForm(prev => ({ ...prev, campaign_start_date: e.target.value }))} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMetrics(null)}>Cancel</Button>
            <Button onClick={saveMetrics} disabled={savingMetrics} className="bg-violet-600 hover:bg-violet-700">
              {savingMetrics ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save Metrics
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consolidated Learnings Dialog */}
      <Dialog open={consolidatedOpen} onOpenChange={setConsolidatedOpen}>
        <DialogContent className="max-w-5xl" data-testid="consolidated-learnings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md shadow-amber-500/30">
                <Lightbulb className="h-4 w-4 text-white" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="leading-tight">Consolidated Learnings</span>
                <span className="text-[11px] font-normal text-slate-500 leading-tight">
                  Actionable pointers synthesized from your filtered creatives.
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>

          {(() => {
            const scope = getConsolidatedScope();
            return (
              <div className="grid grid-cols-12 gap-4">
                {/* Sidebar: Saved Playbooks */}
                <aside className="col-span-4 border-r border-slate-200 pr-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                      <BookMarked className="h-3.5 w-3.5 text-violet-600" />
                      Saved Playbooks
                    </p>
                    <span className="text-[10px] text-slate-400">{playbooks.length}</span>
                  </div>
                  <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="playbooks-sidebar">
                    {playbooks.length === 0 ? (
                      <div className="p-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 text-center">
                        <p className="text-[11px] text-slate-500">No saved playbooks yet</p>
                        <p className="text-[10px] text-slate-400 mt-1">Click "Save as Playbook" after generating to bookmark this scope.</p>
                      </div>
                    ) : playbooks.map(pb => (
                      <div
                        key={pb.id}
                        className="group p-2.5 rounded-lg border border-slate-200 hover:border-violet-300 hover:shadow-sm transition-all cursor-pointer bg-white"
                        onClick={() => loadPlaybook(pb)}
                        data-testid={`playbook-${pb.id}`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-900 truncate" title={pb.name}>{pb.name}</p>
                            {pb.scope_label && (
                              <p className="text-[10px] text-slate-500 truncate mt-0.5" title={pb.scope_label}>{pb.scope_label}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] text-amber-700 font-semibold">{pb.pointers?.length || 0} pointers</span>
                              <span className="text-[9px] text-slate-400">·</span>
                              <span className="text-[9px] text-slate-400">{pb.based_on_count} creative{pb.based_on_count === 1 ? '' : 's'}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deletePlaybook(pb); }}
                            className="w-5 h-5 rounded flex items-center justify-center text-slate-300 hover:bg-red-50 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                            title="Delete playbook"
                            data-testid={`playbook-delete-${pb.id}`}
                          >
                            <Trash className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </aside>

                {/* Main content */}
                <div className="col-span-8 space-y-3">
                  {/* Scope chip — driven by main Creative Log table selection / filters */}
                  <div className="p-3 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100/70 border border-slate-200">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 text-xs flex-wrap">
                        <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700 font-medium" data-testid="consolidated-scope-badge">
                          Scope: {scope.label}
                        </Badge>
                        <span className="text-slate-500">
                          <span className="font-bold text-slate-700">{scope.count}</span> creative{scope.count === 1 ? '' : 's'} with learnings
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-400 italic">
                        {scope.hasManualSelection
                          ? 'Using creatives you selected in the Creative Log'
                          : 'Tip: select creatives in the Creative Log to narrow this scope'}
                      </p>
                    </div>
                  </div>

                  {/* Meta + Action row */}
                  <div className="flex items-center justify-between flex-wrap gap-2 p-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100">
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                      <span className="text-slate-600">
                        <span className="font-bold text-amber-700">{consolidatedData.based_on_count || 0}</span> learning{consolidatedData.based_on_count === 1 ? '' : 's'}
                      </span>
                      {consolidatedData.generated_at && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span className="text-slate-500" title={consolidatedData.generated_at}>
                            {format(new Date(consolidatedData.generated_at), 'MMM d, h:mm a')}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {/* Save as playbook */}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!consolidatedData.pointers?.length}
                        onClick={() => {
                          const def = (consolidatedData.scope_label || scope.label || 'Playbook') + ' · ' + format(new Date(), 'MMM d');
                          setPlaybookName(def);
                          setSavePlaybookOpen(true);
                        }}
                        className="h-8 px-2.5 gap-1.5 border-violet-300 hover:bg-violet-50 text-violet-700"
                        data-testid="consolidated-save-playbook-trigger"
                        title="Save as named playbook"
                      >
                        <Save className="h-3.5 w-3.5" />
                        Save
                      </Button>

                      {/* Export menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!consolidatedData.pointers?.length}
                            className="h-8 px-2.5 gap-1.5 border-amber-300 hover:bg-amber-100/60 text-amber-800"
                            data-testid="consolidated-export-trigger"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Export
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => exportConsolidated('clipboard')} data-testid="consolidated-export-clipboard">
                            <Copy className="h-3.5 w-3.5 mr-2" />
                            Copy to clipboard
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => exportConsolidated('txt')} data-testid="consolidated-export-txt">
                            <FileText className="h-3.5 w-3.5 mr-2" />
                            Download .txt
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => exportConsolidated('csv')} data-testid="consolidated-export-csv">
                            <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
                            Download .csv
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        size="sm"
                        onClick={() => refreshConsolidated()}
                        disabled={consolidatedRefreshing || consolidatedLoading || scope.count === 0}
                        className="h-8 px-3 gap-2 bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="refresh-consolidated-learnings-btn"
                        title={scope.count === 0 ? 'No creatives match — adjust filters' : (consolidatedData.pointers?.length ? 'Re-generate with the latest learnings' : 'Generate consolidated pointers')}
                      >
                        {consolidatedRefreshing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        {consolidatedData.pointers?.length ? 'Regenerate' : 'Generate'}
                      </Button>
                    </div>
                  </div>

                  {/* Futuristic "View Intelligence Report" button — only when infographic exists */}
                  {consolidatedData.infographic && (
                    <button
                      type="button"
                      onClick={() => setReportOpen(true)}
                      className="relative group w-full overflow-hidden rounded-xl border border-violet-300/60 bg-gradient-to-r from-violet-50 via-fuchsia-50/50 to-amber-50 hover:border-violet-400 px-4 py-3 transition-all hover:shadow-[0_10px_30px_-12px_rgba(139,92,246,0.45)] hover:-translate-y-0.5"
                      data-testid="view-intelligence-report-btn"
                    >
                      {/* Scan line */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <div className="absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-violet-200/60 to-transparent skew-x-12 chooser-scan-anim" />
                      </div>
                      <div className="relative flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 via-fuchsia-500 to-amber-400 flex items-center justify-center shadow-md shadow-violet-200 shrink-0 group-hover:scale-110 group-hover:-rotate-3 transition-transform">
                          <Target className="h-4 w-4 text-white" strokeWidth={2} />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-[9.5px] font-bold tracking-[0.18em] uppercase text-violet-700">
                              AI-Generated · Visual playbook
                            </span>
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">
                              NEW
                            </span>
                          </div>
                          <div className="text-[13px] font-bold text-slate-900 leading-tight">
                            View Intelligence Report
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Brand-wise leaderboard, cohorts, themes & landing-page insights — exportable as PNG.
                          </div>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1 text-[11.5px] font-bold text-violet-700 shrink-0 group-hover:gap-1.5 transition-all">
                          Open
                          <span aria-hidden>→</span>
                        </span>
                      </div>
                    </button>
                  )}

                  {/* Pointers list */}
                  <div className="min-h-[200px] max-h-[50vh] overflow-y-auto pr-1">
                    {consolidatedLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
                      </div>
                    ) : consolidatedData.pointers?.length ? (
                      <ul className="space-y-2" data-testid="consolidated-learnings-list">
                        {consolidatedData.pointers.map((p, idx) => (
                          <li
                            key={idx}
                            className="flex gap-3 p-3 rounded-lg bg-white border border-slate-200 hover:border-amber-300 hover:shadow-sm transition-all"
                            data-testid={`consolidated-pointer-${idx}`}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200 flex items-center justify-center">
                                <span className="text-[11px] font-bold text-amber-700">{idx + 1}</span>
                              </div>
                            </div>
                            <p className="flex-1 text-sm text-slate-700 leading-relaxed">{p}</p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="w-14 h-14 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center mb-3">
                          <Lightbulb className="h-6 w-6 text-amber-400" />
                        </div>
                        <p className="text-sm font-medium text-slate-700 mb-1">No consolidated pointers yet</p>
                        <p className="text-xs text-slate-500 max-w-sm">
                          {scope.count === 0
                            ? 'No creatives in scope match the current filters. Try clearing some filters above.'
                            : <>Click <span className="font-semibold text-amber-700">Generate</span> to synthesize {scope.count} learning{scope.count === 1 ? '' : 's'} into a single playbook.</>}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          <DialogFooter className="flex items-center justify-between">
            <p className="text-[10px] text-slate-400 italic">
              Scope reflects your current Creative Log filters. Saved playbooks freeze a scope so you can revisit it anytime.
            </p>
            <Button variant="outline" size="sm" onClick={() => setConsolidatedOpen(false)} data-testid="close-consolidated-learnings-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Playbook Dialog */}
      <Dialog open={savePlaybookOpen} onOpenChange={setSavePlaybookOpen}>
        <DialogContent className="max-w-md" data-testid="save-playbook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookMarked className="h-4 w-4 text-violet-600" />
              Save as Playbook
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Playbook name</Label>
              <Input
                value={playbookName}
                onChange={(e) => setPlaybookName(e.target.value)}
                placeholder="e.g. Wellversed · Q2 Hooks Playbook"
                className="mt-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    savePlaybook();
                  }
                }}
                data-testid="playbook-name-input"
              />
            </div>
            <p className="text-[11px] text-slate-500">
              We'll save the current <span className="font-semibold text-amber-700">{consolidatedData.pointers?.length || 0}</span> actionable pointers along with the scope: <span className="font-medium text-violet-700">{consolidatedData.scope_label || getConsolidatedScope().label}</span>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSavePlaybookOpen(false)} data-testid="save-playbook-cancel">Cancel</Button>
            <Button
              size="sm"
              onClick={savePlaybook}
              disabled={playbookSaving || !playbookName.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="save-playbook-confirm"
            >
              {playbookSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Playbook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Frame Lightbox (video frames) */}
      <Dialog open={lightboxIdx !== null} onOpenChange={(o) => !o && closeLightbox()}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-black border-0">
          {lightboxFrame && (
            <div className="relative">
              <img
                src={`${API_URL}${lightboxFrame.url}`}
                alt={`Frame ${lightboxFrame.label}`}
                className="w-full max-h-[85vh] object-contain"
                data-testid="frame-lightbox-image"
              />
              <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-semibold flex items-center gap-2">
                <Film className="h-3.5 w-3.5" />
                Frame {lightboxFrame.label}
                <span className="text-white/60">·</span>
                <span className="font-mono">{lightboxFrame.timestamp_sec}s</span>
                <span className="text-white/60">·</span>
                <span className="text-white/80">{lightboxIdx + 1} / {lbFrames.length}</span>
              </div>
              {/* Set as thumbnail */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2">
                {selectedCreative?.thumbnail_frame_index === lightboxIdx ? (
                  <div className="px-3 py-1.5 rounded-full bg-emerald-500/90 text-white text-xs font-semibold flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5" /> Current thumbnail
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={setFrameAsThumbnail}
                    disabled={settingThumbnail}
                    className="px-3 py-1.5 rounded-full bg-white/95 hover:bg-white text-slate-900 text-xs font-semibold flex items-center gap-1.5 shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="frame-lightbox-set-thumbnail"
                  >
                    {settingThumbnail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                    Set as thumbnail
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={closeLightbox}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/70 text-white hover:bg-black/90 flex items-center justify-center transition-colors"
                aria-label="Close"
                data-testid="frame-lightbox-close"
              >
                <X className="h-4 w-4" />
              </button>
              {lbFrames.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={prevFrame}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center transition-colors"
                    aria-label="Previous frame"
                    data-testid="frame-lightbox-prev"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={nextFrame}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center transition-colors"
                    aria-label="Next frame"
                    data-testid="frame-lightbox-next"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              {lbFrames.length > 1 && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-8 pb-3 px-3">
                  <div className="flex gap-1.5 justify-center overflow-x-auto" data-testid="frame-lightbox-strip">
                    {lbFrames.map((f, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setLightboxIdx(idx)}
                        className={`relative flex-shrink-0 w-16 h-10 rounded overflow-hidden border-2 transition-all ${idx === lightboxIdx ? 'border-white shadow-lg scale-105' : 'border-white/20 hover:border-white/60 opacity-60 hover:opacity-100'}`}
                        data-testid={`frame-lightbox-strip-${idx}`}
                      >
                        <img src={`${API_URL}${f.url}`} alt={f.label} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Intelligence Report Modal */}
      <IntelligenceReportModal
        open={reportOpen}
        onOpenChange={setReportOpen}
        infographic={consolidatedData.infographic}
        scopeLabel={consolidatedData.scope_label}
      />
    </div>
  );
};

export default CreativeLogPage;
